// src/api-client.ts
import {
  AnnouncementList,
  CalendarEvent,
  CalendarList,
  AnnouncementStats,
  Channel,
  ChannelList,
  CreateAnnouncementRequest,
  Announcement,
  ApiError,
  ArchivePage,
  AuthResponse,
  AuthTokens,
  BotSummary,
  BotTokenResponse,
  ClaimKeyPackagesResponse,
  ConversationSummary,
  CreateFileResponse,
  GroupInfoBody,
  DeviceListResponse,
  KeyBackup,
  KeyPackageCount,
  LibraryListResponse,
  LibraryQuotasResponse,
  OrgSettingsResponse,
  LinkRequestResponse,
  CreateLibraryFileResponse,
  MeResponse,
  MessagesPage,
  PendingWelcome,
  PrefsResponse,
  Project,
  SendMessageResponse,
  SentAnnouncement,
  UserSummary,
  IceServersResponse,
  VapidKeyResponse
} from "@swchat/protocol";
import { z } from "zod";
var SwChatApiError = class extends Error {
  constructor(status, code, message, retryAfter, body) {
    super(message);
    this.status = status;
    this.code = code;
    this.retryAfter = retryAfter;
    this.body = body;
    this.name = "SwChatApiError";
  }
  status;
  code;
  retryAfter;
  body;
};
function isRateLimited(e) {
  return e instanceof SwChatApiError && e.status === 429;
}
function retryAfterOf(res) {
  const v = Number(res.headers.get("Retry-After"));
  return Number.isFinite(v) && v >= 0 && res.headers.has("Retry-After") ? v : void 0;
}
var Empty = z.unknown();
var Ids = z.array(z.uuid());
var ApiClient = class {
  constructor(opts) {
    this.opts = opts;
    this.fetchImpl = opts.fetch ?? globalThis.fetch.bind(globalThis);
  }
  opts;
  fetchImpl;
  // --- Auth ---
  portalExchange = (req) => this.call("POST", "/auth/portal-exchange", AuthResponse, req, false);
  portalCode = (req) => this.call("POST", "/auth/portal-code", AuthResponse, req, false);
  /** agent-gateway: đăng nhập bot bằng token (thiết bị duy nhất của bot). */
  botLogin = (req) => this.call("POST", "/auth/bot", AuthResponse, req, false);
  refresh = (refresh) => this.call("POST", "/auth/refresh", AuthTokens, { refresh }, false);
  logout = () => this.call("POST", "/auth/logout", Empty);
  // --- Bot (quản trị) ---
  bots = (scope = "mine") => this.call("GET", `/bots?scope=${scope}`, z.array(BotSummary));
  createBot = (req) => this.call("POST", "/bots", BotSummary, req);
  updateBot = (id, req) => this.call("PATCH", `/bots/${id}`, BotSummary, req);
  botToken = (id) => this.call("POST", `/bots/${id}/token`, BotTokenResponse);
  resetBot = (id) => this.call("POST", `/bots/${id}/reset`, BotSummary);
  disableBot = (id) => this.call("DELETE", `/bots/${id}`, Empty);
  // --- Danh tính ---
  me = () => this.call("GET", "/me", MeResponse);
  /** Đổi tên hiển thị / ảnh đại diện của mình (`null` = quay về tên IdP / bỏ ảnh). */
  updateProfile = (req) => this.call("PATCH", "/me/profile", UserSummary, req);
  users = () => this.call("GET", "/users", z.array(UserSummary));
  deviceList = (userId) => this.call("GET", `/users/${userId}/device-list`, DeviceListResponse);
  putDeviceList = (list) => this.call("PUT", "/me/device-list", z.object({ version: z.number() }), list);
  keyBackup = () => this.call("GET", "/me/key-backup", KeyBackup);
  putKeyBackup = (blob) => this.call("PUT", "/me/key-backup", Empty, { blob });
  rejectDevice = (deviceId) => this.call("DELETE", `/devices/${deviceId}`, Empty);
  createLink = (ephemeralPub) => this.call("POST", "/devices/link", LinkRequestResponse, { ephemeral_pub: ephemeralPub });
  getLink = (id) => this.call("GET", `/devices/link/${id}`, LinkRequestResponse);
  respondLink = (id, envelope) => this.call("POST", `/devices/link/${id}/respond`, Empty, { envelope });
  // --- Thông báo công ty & tùy chọn cá nhân ---
  announcements = (before, limit = 30, channelId) => this.call("GET", `/announcements?limit=${limit}${before ? `&before=${encodeURIComponent(before)}` : ""}${channelId ? `&channel_id=${channelId}` : ""}`, AnnouncementList);
  // Kênh thông báo: danh sách, tạo/gán/cài đặt (quản trị), ghim / tắt theo dõi (riêng từng người).
  announcementChannels = () => this.call("GET", "/announcement-channels", ChannelList);
  createChannel = (req) => this.call("POST", "/announcement-channels", Channel, req);
  updateChannel = (id, req) => this.call("PATCH", `/announcement-channels/${id}`, Channel, req);
  setChannelPrefs = (id, req) => this.call("PUT", `/announcement-channels/${id}/me`, Channel, req);
  createAnnouncement = (req) => this.call("POST", "/announcements", Announcement, req);
  sentAnnouncements = () => this.call("GET", "/announcements/sent", z.array(SentAnnouncement));
  // Đính kèm thông báo (không E2EE): tải lên theo khối ≤ 1 MB, xem / tải bằng GET có xác thực.
  createAnnouncementAttachment = (name, mime, size) => this.call("POST", "/announcements/attachments", z.object({ id: z.string() }), { name, mime, size });
  putAnnouncementChunk = async (id, index, chunk) => {
    await this.raw("PUT", `/announcements/attachments/${id}/chunks/${index}`, chunk);
  };
  completeAnnouncementAttachment = (id) => this.call("POST", `/announcements/attachments/${id}/complete`, Empty);
  downloadAnnouncementAttachment = async (id) => (await this.raw("GET", `/announcements/attachments/${id}`)).blob();
  announcementStats = (id) => this.call("GET", `/announcements/${id}/stats`, AnnouncementStats);
  markAnnouncementsRead = (ids) => this.call("POST", "/announcements/read", Empty, { ids });
  ackAnnouncement = (id) => this.call("POST", `/announcements/${id}/ack`, Empty);
  setAnnouncementReaction = (id, reaction) => this.call("PUT", `/announcements/${id}/reaction`, Empty, { reaction });
  getPrefs = () => this.call("GET", "/me/prefs", PrefsResponse);
  putPrefs = (baseVersion, blob) => this.call("PUT", "/me/prefs", z.object({ version: z.number().int() }), { base_version: baseVersion, blob });
  // --- Khóa ---
  uploadKeyPackages = (req) => this.call("POST", "/keys/packages", KeyPackageCount, req);
  keyPackageCount = () => this.call("GET", "/keys/packages/count", KeyPackageCount);
  claimKeyPackages = (userIds) => this.call("POST", "/keys/claim", ClaimKeyPackagesResponse, { user_ids: userIds });
  // --- Hội thoại ---
  conversations = () => this.call("GET", "/conversations", z.array(ConversationSummary));
  allConversations = () => this.call("GET", "/conversations/all", z.array(ConversationSummary));
  groupInfo = (id) => this.call("GET", `/conversations/${id}/group-info`, GroupInfoBody);
  putGroupInfo = (id, req) => this.call("PUT", `/conversations/${id}/group-info`, Empty, req);
  externalJoin = (id, req) => this.call("POST", `/conversations/${id}/external-join`, SendMessageResponse, req);
  setMlsFeatures = (req) => this.call("PUT", "/devices/me/mls-features", Empty, req);
  conversation = (id) => this.call("GET", `/conversations/${id}`, ConversationSummary);
  createConversation = (req) => this.call("POST", "/conversations", ConversationSummary, req);
  invite = (id, userIds, treeSynced) => this.call("POST", `/conversations/${id}/members`, Empty, { user_ids: userIds, ...treeSynced && { tree_synced: true } });
  /** Xóa thành viên (admin/owner) hoặc rời nhóm khi `userId` là chính mình. */
  // Lịch & nhắc hẹn
  calendar = (from, to, projectId) => this.call("GET", `/calendar?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}${projectId ? `&project_id=${encodeURIComponent(projectId)}` : ""}`, CalendarList);
  createCalendarEvent = (req) => this.call("POST", "/calendar", CalendarEvent, req);
  updateCalendarEvent = (id, req) => this.call("PATCH", `/calendar/${id}`, CalendarEvent, req);
  deleteCalendarEvent = (id) => this.call("DELETE", `/calendar/${id}`, Empty);
  hideConversation = (id) => this.call("DELETE", `/conversations/${id}/mine`, Empty);
  deleteConversation = (id) => this.call("DELETE", `/conversations/${id}`, Empty);
  removeMember = (id, userId) => this.call("DELETE", `/conversations/${id}/members/${userId}`, Empty);
  updateRole = (id, userId, role) => this.call("PATCH", `/conversations/${id}/members/${userId}`, Empty, { role });
  send = (id, req) => this.call("POST", `/conversations/${id}/messages`, SendMessageResponse, req);
  messages = (id, afterSeq, limit = 100) => this.call("GET", `/conversations/${id}/messages?after_seq=${afterSeq}&limit=${limit}`, MessagesPage);
  deleteMessages = (id, seqs) => this.call("POST", `/conversations/${id}/messages/delete`, Empty, { seqs });
  welcomes = () => this.call("GET", "/welcomes", z.array(PendingWelcome));
  ackWelcome = (id) => this.call("POST", `/welcomes/${id}/ack`, Empty);
  putCursors = (cursors) => this.call("PUT", "/cursors", Empty, cursors);
  // --- File (ciphertext theo khối; docs/06-api.md §Files) ---
  /** `size` = kích thước ciphertext (gồm tag mỗi khối). */
  createFile = (conversationId, size) => this.call("POST", "/files", CreateFileResponse, { conversation_id: conversationId, size });
  putFileChunk = async (fileId, index, chunk) => {
    await this.raw("PUT", `/files/${fileId}/chunks/${index}`, chunk);
  };
  completeFile = (fileId, sha256) => this.call("POST", `/files/${fileId}/complete`, Empty, { sha256 });
  /** Tải toàn bộ ciphertext của file (người đã rời / người ngoài → 404). */
  downloadFile = async (fileId) => {
    const res = await this.raw("GET", `/files/${fileId}`);
    return new Uint8Array(await res.arrayBuffer());
  };
  // --- Archive ---
  uploadArchive = (entries) => this.call("POST", "/archive", Empty, { entries });
  archivedConversations = () => this.call("GET", "/archive", Ids);
  archive = (conversationId, afterSeq, limit = 400) => this.call("GET", `/archive/${conversationId}?after_seq=${afterSeq}&limit=${limit}`, ArchivePage);
  // --- Dự án ---
  syncProjects = (portalToken) => this.call("POST", "/projects/sync", z.array(Project), { portal_token: portalToken });
  projects = () => this.call("GET", "/projects", z.array(Project));
  projectMembers = (id) => this.call("GET", `/projects/${encodeURIComponent(id)}/members`, Ids);
  // --- Web Push (Q4) ---
  /** Push tắt trên server → `SwChatApiError` 404 `push_disabled`. */
  iceServers = () => this.call("GET", "/calls/ice", IceServersResponse);
  vapidKey = () => this.call("GET", "/push/vapid-key", VapidKeyResponse);
  putPushSubscription = (sub) => this.call("PUT", "/push/subscription", Empty, sub);
  deletePushSubscription = () => this.call("DELETE", "/push/subscription", Empty);
  /** App mobile (Android): token FCM của thiết bị. Push tắt trên server → 404 `push_disabled`. */
  putFcmToken = (token) => this.call("PUT", "/push/fcm-token", Empty, { token });
  deleteFcmToken = () => this.call("DELETE", "/push/fcm-token", Empty);
  // --- Lưu trữ cá nhân (ciphertext theo khối; docs/06-api.md §Library) ---
  library = () => this.call("GET", "/library", LibraryListResponse);
  /** `size` = kích thước ciphertext (gồm tag mỗi khối). Vượt hạn mức → 413. */
  createLibraryFile = (size) => this.call("POST", "/library/files", CreateLibraryFileResponse, { size });
  putLibraryChunk = async (fileId, index, chunk) => {
    await this.raw("PUT", `/library/files/${fileId}/chunks/${index}`, chunk);
  };
  completeLibraryFile = (fileId, sha256, meta) => this.call("POST", `/library/files/${fileId}/complete`, Empty, { sha256, meta });
  downloadLibraryFile = async (fileId) => new Uint8Array(await (await this.raw("GET", `/library/files/${fileId}`)).arrayBuffer());
  updateLibraryFile = (fileId, baseVersion, meta) => this.call("PATCH", `/library/files/${fileId}`, z.object({ meta_version: z.number().int() }), { base_version: baseVersion, meta });
  deleteLibraryFile = (fileId) => this.call("DELETE", `/library/files/${fileId}`, Empty);
  libraryQuotas = () => this.call("GET", "/library/quotas", LibraryQuotasResponse);
  putLibraryQuota = (role, quotaBytes) => this.call("PUT", "/library/quotas", Empty, { role, quota_bytes: quotaBytes });
  /** Cài đặt toàn tổ chức: mọi người đọc, chỉ quản trị viên ghi (`can_manage`). */
  orgSettings = () => this.call("GET", "/settings/org", OrgSettingsResponse);
  updateOrgSettings = (patch) => this.call("PUT", "/settings/org", OrgSettingsResponse, patch);
  /** Request nhị phân (`application/octet-stream`); lỗi được ném như `call`. */
  async raw(method, path, body) {
    const headers = {};
    if (body !== void 0) headers["Content-Type"] = "application/octet-stream";
    const token = await this.opts.getAccessToken?.();
    if (token) headers.Authorization = `Bearer ${token}`;
    const res = await this.fetchImpl(`${this.opts.baseUrl}/v1${path}`, { method, headers, body });
    if (!res.ok) await this.throwError(res);
    return res;
  }
  async throwError(res) {
    const text = await res.text();
    let json;
    try {
      json = text ? JSON.parse(text) : void 0;
    } catch {
      json = void 0;
    }
    const parsed = ApiError.safeParse(json);
    const err = parsed.success ? parsed.data.error : { code: "invalid_request", message: res.statusText };
    throw new SwChatApiError(res.status, err.code, err.message, retryAfterOf(res), json);
  }
  async call(method, path, schema, body, auth = true) {
    const headers = { Accept: "application/json" };
    if (body !== void 0) headers["Content-Type"] = "application/json";
    if (auth) {
      const token = await this.opts.getAccessToken?.();
      if (token) headers.Authorization = `Bearer ${token}`;
    }
    const res = await this.fetchImpl(`${this.opts.baseUrl}/v1${path}`, {
      method,
      headers,
      body: body === void 0 ? void 0 : JSON.stringify(body)
    });
    if (!res.ok) await this.throwError(res);
    const text = await res.text();
    const json = text ? JSON.parse(text) : void 0;
    return schema.parse(json);
  }
};

// src/client.ts
import {
  emptyPrefs,
  isAvatarWebp,
  MAX_PREFS_BYTES,
  MessagePayload,
  PersonalPrefs,
  PrefsResponse as PrefsResponse2,
  ServerEvent,
  ANNOUNCEMENT_CHUNK_BYTES,
  announcementChunkCount
} from "@swchat/protocol";

// src/crypto.ts
var FILE_CHUNK_SIZE = 1024 * 1024;
var FILE_TAG_SIZE = 16;

// src/entities.ts
import { ENTITY_TYPES, MAX_ENTITIES } from "@swchat/protocol";
var SAFE_URL = /^(https?:\/\/|mailto:)[^\s]+$/i;
function sanitizeEntities(text, entities) {
  if (!entities?.length) return void 0;
  const known = new Set(ENTITY_TYPES);
  const out = [];
  for (const e of entities) {
    if (!known.has(e.t) || e.o < 0 || e.l <= 0 || e.o + e.l > text.length) continue;
    if (e.t === "link") {
      if (!e.url || !SAFE_URL.test(e.url)) continue;
      out.push({ t: "link", o: e.o, l: e.l, url: e.url });
    } else {
      out.push({ t: e.t, o: e.o, l: e.l });
    }
    if (out.length >= MAX_ENTITIES) break;
  }
  return out.length ? out : void 0;
}

// src/encoding.ts
function toB64(bytes) {
  let bin = "";
  for (let i = 0; i < bytes.length; i += 32768) bin += String.fromCharCode(...bytes.subarray(i, i + 32768));
  return btoa(bin).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}
function fromB64(s) {
  const bin = atob(s.replace(/-/g, "+").replace(/_/g, "/") + "=".repeat((4 - s.length % 4) % 4));
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}
function bytesEqual(a, b) {
  return a.length === b.length && a.every((v, i) => v === b[i]);
}
var encoder = new TextEncoder();
var decoder = new TextDecoder();
var utf8 = { encode: (s) => encoder.encode(s), decode: (b) => decoder.decode(b) };
function uuidv7(now = Date.now()) {
  const b = crypto.getRandomValues(new Uint8Array(16));
  for (let i = 0; i < 6; i++) b[i] = Math.floor(now / 2 ** (8 * (5 - i))) & 255;
  b[6] = b[6] & 15 | 112;
  b[8] = b[8] & 63 | 128;
  const h = [...b].map((x) => x.toString(16).padStart(2, "0")).join("");
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
}
function uuidv4() {
  return crypto.randomUUID();
}

// src/library.ts
var addressOf = (id) => `~lib:${id}`;
var PersonalLibrary = class {
  constructor(api, crypto2, mls) {
    this.api = api;
    this.crypto = crypto2;
    this.mls = mls;
  }
  api;
  crypto;
  mls;
  open(id, version, meta) {
    return JSON.parse(utf8.decode(this.mls().archiveOpen(addressOf(id), version, fromB64(meta))));
  }
  seal(id, version, meta) {
    return toB64(this.mls().archiveSeal(addressOf(id), version, utf8.encode(JSON.stringify(meta))));
  }
  async list() {
    const r = await this.api.library();
    const files = [];
    let unreadable = 0;
    for (const f of r.files) {
      try {
        files.push({ id: f.id, storedSize: f.size, createdAt: f.created_at, metaVersion: f.meta_version, meta: this.open(f.id, f.meta_version, f.meta) });
      } catch {
        unreadable++;
      }
    }
    return { files, used: r.used_bytes, quota: r.quota_bytes, quotaRole: r.quota_role, isAdmin: r.is_admin, unreadable };
  }
  /** Tải một file lên (mã hóa từng khối 1 MiB trên máy). Vượt hạn mức → `SwChatApiError` 413. */
  async upload(file, onProgress) {
    const size = file.data instanceof Uint8Array ? file.data.length : file.data.size;
    const count = Math.max(1, Math.ceil(size / FILE_CHUNK_SIZE));
    const { key, prefix } = this.crypto.newFileKey();
    const { file_id } = await this.api.createLibraryFile(size + count * FILE_TAG_SIZE);
    const hasher = this.crypto.createFileHasher();
    let sha256;
    try {
      onProgress?.(0);
      for (let i = 0; i < count; i++) {
        const start = i * FILE_CHUNK_SIZE;
        const end = Math.min(start + FILE_CHUNK_SIZE, size);
        const plain = file.data instanceof Uint8Array ? file.data.subarray(start, end) : new Uint8Array(await file.data.slice(start, end).arrayBuffer());
        const ct = this.crypto.encryptFileChunk(key, prefix, file_id, i, i === count - 1, plain);
        hasher.update(ct);
        await this.putChunk(file_id, i, ct);
        onProgress?.((i + 1) / count);
      }
      sha256 = hasher.finalize();
    } catch (e) {
      await this.api.deleteLibraryFile(file_id).catch(() => void 0);
      throw e;
    } finally {
      hasher.free?.();
    }
    const meta = { name: file.name.slice(0, 255), mime: file.mime || "application/octet-stream", size, key: toB64(key), prefix: toB64(prefix), sha256: toB64(sha256) };
    try {
      await this.api.completeLibraryFile(file_id, toB64(sha256), this.seal(file_id, 1, meta));
    } catch (e) {
      await this.api.deleteLibraryFile(file_id).catch(() => void 0);
      throw e;
    }
    return file_id;
  }
  /** PUT khối, thử lại khi server báo bận (429). */
  async putChunk(id, index, ct) {
    for (let attempt = 1; ; attempt++) {
      try {
        return await this.api.putLibraryChunk(id, index, ct);
      } catch (e) {
        if (!(e instanceof SwChatApiError && e.status === 429) || attempt >= 5) throw e;
        await new Promise((r) => setTimeout(r, Math.max(1, e.retryAfter ?? 1) * 1e3));
      }
    }
  }
  /** Tải và giải mã toàn bộ nội dung một file (kiểm sha256 + từng khối). */
  async download(entry, onProgress) {
    const ct = await this.api.downloadLibraryFile(entry.id);
    const hasher = this.crypto.createFileHasher();
    let digest;
    try {
      const SLICE = 1024 * 1024;
      for (let off = 0; off < ct.length; off += SLICE) hasher.update(ct.subarray(off, off + SLICE));
      digest = hasher.finalize();
    } finally {
      hasher.free?.();
    }
    if (toB64(digest) !== entry.meta.sha256) throw new Error("File b\u1ECB thay \u0111\u1ED5i (sha256 kh\xF4ng kh\u1EDBp)");
    const encChunk = FILE_CHUNK_SIZE + FILE_TAG_SIZE;
    const count = Math.max(1, Math.ceil(ct.length / encChunk));
    if (ct.length - count * FILE_TAG_SIZE !== entry.meta.size) throw new Error("K\xEDch th\u01B0\u1EDBc file kh\xF4ng kh\u1EDBp");
    const key = fromB64(entry.meta.key);
    const prefix = fromB64(entry.meta.prefix);
    const out = new Uint8Array(entry.meta.size);
    for (let i = 0; i < count; i++) {
      const plain = this.crypto.decryptFileChunk(key, prefix, entry.id, i, i === count - 1, ct.subarray(i * encChunk, (i + 1) * encChunk));
      out.set(plain, i * FILE_CHUNK_SIZE);
      onProgress?.((i + 1) / count);
    }
    return out;
  }
  /** Đổi tên (meta mã hóa lại với phiên bản kế tiếp). Trả phiên bản mới. */
  async rename(entry, name) {
    const next = entry.metaVersion + 1;
    const { meta_version } = await this.api.updateLibraryFile(entry.id, entry.metaVersion, this.seal(entry.id, next, { ...entry.meta, name: name.slice(0, 255) }));
    return meta_version;
  }
  remove = (id) => this.api.deleteLibraryFile(id);
  // --- Quản trị ---
  quotas = () => this.api.libraryQuotas();
  setQuota = (role, quotaBytes) => this.api.putLibraryQuota(role, quotaBytes);
};

// src/client.ts
var MAX_FILE_SIZE = 100 * FILE_CHUNK_SIZE;
var KEY_PACKAGE_TARGET = 50;
var PREFS_ADDRESS = "~prefs";
var KEY_PACKAGE_LOW = 10;
var TAG_RE = /#([A-Z0-9][A-Z0-9_-]{1,31})/g;
var DELIVERED_DEBOUNCE_MS = 1e3;
var READ_RETRY_BASE_MS = 2e3;
var READ_RETRY_MAX_MS = 6e4;
var RECEIPT_TTL_MS = 14 * 864e5;
var CALL_SIGNAL_TTL_MS = 36e5;
var MAX_CLOCK_SKEW_MS = 5 * 6e4;
var VISIBLE_TYPES = /* @__PURE__ */ new Set(["text", "file", "poll", "location", "call", "undecryptable"]);
var CALL_SIGNAL_MAX_AGE_MS = 9e4;
var RATE_RETRY_MAX = 5;
var ACTIVITY_LIMIT = 500;
var LEAVE_MARK_TTL_MS = 2 * 6e4;
var THREAD_PREVIEW_PREFIX = "Tr\u1EA3 l\u1EDDi trong lu\u1ED3ng: ";
var RATE_RETRY_DEFAULT_SEC = 1;
var RATE_RETRY_MAX_SEC = 30;
function isPermanentApiError(e) {
  return e instanceof SwChatApiError && e.status >= 400 && e.status < 500 && ![401, 408, 409, 429].includes(e.status);
}
var UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
function normalizeSearch(text) {
  return [...text].map(foldChar).join("");
}
function trustedTs(claimed, serverTime) {
  const server = Date.parse(serverTime);
  if (!Number.isFinite(server)) return claimed;
  return Math.min(server, Math.max(claimed, server - MAX_CLOCK_SKEW_MS));
}
var NAME_CHAR_RE = /[\p{L}\p{N}\p{M}_]/u;
function detectMentions(text, members) {
  const consumed = [];
  const found = /* @__PURE__ */ new Set();
  const byLength = members.filter((m) => m.name).sort((a, b) => b.name.length - a.name.length);
  for (const m of byLength) {
    const needle = `@${m.name}`;
    for (let at = text.indexOf(needle); at >= 0; at = text.indexOf(needle, at + 1)) {
      const end = at + needle.length;
      if (at > 0 && NAME_CHAR_RE.test(text[at - 1])) continue;
      if (end < text.length && NAME_CHAR_RE.test(text[end])) continue;
      if (consumed.some(([s, e]) => at < e && end > s)) continue;
      consumed.push([at, end]);
      found.add(m.id);
    }
  }
  return members.filter((m) => found.has(m.id)).map((m) => m.id);
}
function hasAllMention(text) {
  const re = /@all/gi;
  for (let m = re.exec(text); m; m = re.exec(text)) {
    const end = m.index + 4;
    if (m.index > 0 && NAME_CHAR_RE.test(text[m.index - 1])) continue;
    if (end < text.length && NAME_CHAR_RE.test(text[end])) continue;
    return true;
  }
  return false;
}
function foldChar(ch) {
  return ch.normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[đĐ]/g, "d").toLowerCase();
}
function foldIndexOf(text, needle) {
  let folded = "";
  const origin = [];
  let pos = 0;
  for (const ch of text) {
    const f = foldChar(ch);
    for (let i = 0; i < f.length; i++) origin.push(pos);
    folded += f;
    pos += ch.length;
  }
  const at = folded.indexOf(needle);
  return at < 0 ? -1 : origin[at];
}
function snippetAround(text, at, length, context = 40) {
  const start = Math.max(0, at - context);
  const end = Math.min(text.length, at + length + context);
  return `${start > 0 ? "\u2026" : ""}${text.slice(start, end).replace(/\s+/g, " ")}${end < text.length ? "\u2026" : ""}`;
}
var SwChatClient = class {
  constructor(opts) {
    this.opts = opts;
    this.log = opts.logger ?? console;
    this.api = new ApiClient({ baseUrl: opts.baseUrl, fetch: opts.fetch, getAccessToken: () => this.accessToken() });
  }
  opts;
  api;
  session;
  mls;
  statusValue = "signed_out";
  conversations = /* @__PURE__ */ new Map();
  users = /* @__PURE__ */ new Map();
  deviceLists = /* @__PURE__ */ new Map();
  listeners = /* @__PURE__ */ new Map();
  chain = Promise.resolve();
  refreshing;
  ws;
  wsAttempts = 0;
  /** Hẹn kết nối lại realtime sau khi WebSocket đóng (hủy khi `shutdown`). */
  wsRetry;
  stopped = true;
  /**
   * Đã `shutdown` (chưa đăng nhập/`start` lại). Khác `stopped` (cũng đúng khi chưa `start`, vd. thiết bị chờ liên kết vẫn
   * nối realtime): client đã dừng không được tự kết nối lại — trước đây thiết bị chưa `ready` (chờ liên kết/khôi phục)
   * sau `shutdown` vẫn hẹn kết nối lại mãi tới server đã tắt, giữ tiến trình (test E2E) không thoát được.
   */
  disposed = false;
  /** Đã báo server lõi crypto xử lý được External Commit (mỗi phiên một lần). */
  mlsFeaturesDeclared = false;
  pendingLink;
  repairTimers = /* @__PURE__ */ new Map();
  /** Hẹn gỡ thiết bị của người đã rời/bị xóa khỏi nhóm MLS (theo hội thoại). */
  cleanupTimers = /* @__PURE__ */ new Map();
  projects;
  /** Tăng mỗi lần bỏ bộ đệm dự án: kết quả `getProjects` đang chờ từ trước (phiên cũ) không được ghi lại vào bộ đệm. */
  projectsGen = 0;
  deliveredTimers = /* @__PURE__ */ new Map();
  /** Hẹn gửi lại receipt `read` lỗi (theo hội thoại) và số lần đã thử (tính backoff). */
  readRetryTimers = /* @__PURE__ */ new Map();
  readRetryAttempts = /* @__PURE__ */ new Map();
  /** Tóm tắt hội thoại (tin cuối, số chưa đọc) — xóa khi hội thoại có thay đổi. */
  summaryCache = /* @__PURE__ */ new Map();
  /** Tăng mỗi lần hội thoại thay đổi: tóm tắt tính xong sau một thay đổi thì không ghi vào cache (tránh cache cũ). */
  summaryGen = /* @__PURE__ */ new Map();
  log;
  // =====================================================================
  // Sự kiện & trạng thái
  // =====================================================================
  on(event, fn) {
    let set = this.listeners.get(event);
    if (!set) this.listeners.set(event, set = /* @__PURE__ */ new Set());
    set.add(fn);
    return () => set.delete(fn);
  }
  emit(event, ...args) {
    if (event === "messages") {
      const id = args[0];
      this.summaryCache.delete(id);
      this.summaryGen.set(id, (this.summaryGen.get(id) ?? 0) + 1);
    }
    for (const fn of this.listeners.get(event) ?? []) {
      try {
        fn(...args);
      } catch (e) {
        this.log.warn(`listener '${event}' l\u1ED7i: ${e instanceof Error ? e.message : e}`);
      }
    }
  }
  get status() {
    return this.statusValue;
  }
  setStatus(status) {
    if (this.statusValue === status) return;
    this.statusValue = status;
    this.emit("status", status);
  }
  get userId() {
    return this.session?.user_id;
  }
  get deviceId() {
    return this.session?.device_id;
  }
  get displayName() {
    return this.session?.display_name;
  }
  /** Chạy tuần tự các thao tác chạm vào trạng thái MLS, rồi lưu snapshot. */
  serial(fn) {
    const run = this.chain.then(async () => {
      try {
        return await fn();
      } finally {
        await this.persist();
      }
    });
    this.chain = run.catch(() => void 0);
    return run;
  }
  async persist() {
    if (!this.session || !this.mls) return;
    this.session.crypto_state = this.mls.exportState();
    await this.opts.store.saveSession(this.session);
  }
  requireMls() {
    if (!this.mls || !this.session) throw new Error("Ch\u01B0a \u0111\u0103ng nh\u1EADp");
    return this.mls;
  }
  // =====================================================================
  // Phiên
  // =====================================================================
  /** Khôi phục phiên đã lưu (nếu có) và xác định trạng thái. */
  async init() {
    const saved = await this.opts.store.loadSession();
    if (!saved) return this.statusValue;
    this.session = saved;
    this.mls = saved.crypto_state ? this.opts.crypto.importClient(saved.crypto_state) : this.opts.crypto.createClient(saved.user_id, saved.device_id);
    try {
      const me = await this.meWithRefresh();
      this.setStatus(this.statusFor(me.device.status, me.user.has_identity));
    } catch (e) {
      if (e instanceof SwChatApiError && e.code === "device_revoked") {
        await this.opts.store.clear();
        this.session = void 0;
        this.mls = void 0;
        this.setStatus("revoked");
        return this.statusValue;
      }
      if (e instanceof SwChatApiError && e.status === 401) {
        this.setStatus("signed_out");
        return this.statusValue;
      }
      this.setStatus(this.mls.hasIdentity ? "ready" : "needs_recovery");
    }
    if (this.statusValue === "ready") await this.start();
    return this.statusValue;
  }
  /** `/me`; access token bị từ chối (vd. server đổi JWT_SECRET) thì refresh một lần rồi thử lại. */
  async meWithRefresh() {
    try {
      return await this.api.me();
    } catch (e) {
      if (!(e instanceof SwChatApiError && e.status === 401 && e.code !== "device_revoked") || !this.session) throw e;
      this.session.tokens = { ...this.session.tokens, access_expires_at: 0 };
      return this.api.me();
    }
  }
  statusFor(deviceStatus, userHasIdentity) {
    const hasKeys = this.mls?.hasIdentity ?? false;
    if (deviceStatus === "active" && hasKeys) return "ready";
    if (deviceStatus === "active" && !userHasIdentity) return "needs_identity";
    return "needs_recovery";
  }
  /** Nhúng trong portal: token người dùng từ 7w_portal. */
  async loginWithPortalToken(portalToken) {
    return this.completeLogin(
      await this.api.portalExchange({ portal_token: portalToken, device: this.opts.device, device_id: this.session?.device_id })
    );
  }
  /**
   * agent-gateway: đăng nhập bằng token bot (`<bot_id>:<bí mật>`). Thiết bị đầu tiên của bot hoạt động ngay → trạng thái
   * `needs_identity`: gateway gọi `createIdentity()` (Recovery Key bỏ đi — bot không có người nhập khóa; mất kho khóa thì chủ
   * bot đặt lại bot). Lần sau dùng lại thiết bị đã lưu (`device_id`).
   */
  async loginAsBot(token) {
    return this.completeLogin(await this.api.botLogin({ token, device_id: this.session?.device_id }));
  }
  /** Web riêng / mobile: authorization code + PKCE từ 7w_portal. */
  async loginWithCode(code, codeVerifier, redirectUri) {
    return this.completeLogin(
      await this.api.portalCode({
        code,
        code_verifier: codeVerifier,
        redirect_uri: redirectUri,
        device: this.opts.device,
        device_id: this.session?.device_id
      })
    );
  }
  async completeLogin(res) {
    if (this.session) {
      await this.api.logout().catch(() => void 0);
      if (this.session.user_id !== res.user.id) {
        await this.shutdown();
        await this.opts.store.clear();
        this.session = void 0;
        this.mls = void 0;
        this.conversations.clear();
        this.deviceLists.clear();
      }
    }
    this.clearProjects();
    const sameDevice = this.session?.device_id === res.device.id && this.mls;
    this.session = {
      user_id: res.user.id,
      device_id: res.device.id,
      display_name: res.user.display_name,
      tokens: { access: res.access, refresh: res.refresh, access_expires_at: res.access_expires_at }
    };
    if (!sameDevice) this.mls = this.opts.crypto.createClient(res.user.id, res.device.id);
    await this.persist();
    this.setStatus(this.statusFor(res.device.status, res.has_identity));
    this.disposed = false;
    if (this.statusValue === "ready") await this.start();
    else this.connectRealtime();
    return this.statusValue;
  }
  async logout() {
    await this.api.logout().catch(() => void 0);
    await this.shutdown();
    await this.opts.store.clear();
    this.session = void 0;
    this.mls = void 0;
    this.conversations.clear();
    this.clearProjects();
    this.setStatus("signed_out");
  }
  async accessToken() {
    const s = this.session;
    if (!s) return void 0;
    if (s.tokens.access_expires_at - 3e4 > Date.now()) return s.tokens.access;
    this.refreshing ??= (async () => {
      try {
        s.tokens = await this.retryRateLimited("refresh token", () => this.api.refresh(s.tokens.refresh));
        await this.opts.store.saveSession(s);
      } finally {
        this.refreshing = void 0;
      }
    })();
    await this.refreshing;
    return s.tokens.access;
  }
  // =====================================================================
  // Danh tính & thiết bị
  // =====================================================================
  /** Thiết bị đầu tiên của người dùng. Trả Recovery Key — hiển thị MỘT lần cho người dùng lưu. */
  async createIdentity() {
    if (this.statusValue !== "needs_identity") throw new Error("Kh\xF4ng \u1EDF tr\u1EA1ng th\xE1i t\u1EA1o danh t\xEDnh");
    const recoveryKey = await this.serial(async () => {
      const mls = this.requireMls();
      const { recovery_key, key_backup } = mls.createIdentity();
      await this.api.putKeyBackup(toB64(key_backup));
      const signed = mls.signDeviceList(1, [mls.ownDeviceEntry()]);
      await this.api.putDeviceList({ payload: toB64(signed.payload), signature: toB64(signed.signature) });
      await this.opts.store.setPinnedUsk(this.session.user_id, toB64(mls.uskPublicKey));
      return recovery_key;
    });
    await this.becomeReady();
    return recoveryKey;
  }
  /** Thiết bị mới: khôi phục bằng Recovery Key, sau đó tải lịch sử từ archive. */
  async restoreWithRecoveryKey(recoveryKey) {
    await this.serial(async () => {
      const { blob } = await this.api.keyBackup();
      this.requireMls().restoreIdentity(recoveryKey, fromB64(blob));
    });
    await this.activateThisDevice();
  }
  /** Thiết bị mới: tạo yêu cầu liên kết; hiển thị `qr` và `code`, rồi gọi `waitForLink`. */
  async beginLink() {
    const pub = this.requireMls().beginLink();
    const link = await this.api.createLink(toB64(pub));
    return { linkId: link.id, qr: JSON.stringify({ l: link.id, k: toB64(pub) }), code: this.opts.crypto.linkCode(pub) };
  }
  async waitForLink(linkId, timeoutMs = 5 * 6e4) {
    const deadline = Date.now() + timeoutMs;
    while (Date.now() < deadline) {
      const link = await this.api.getLink(linkId);
      if (link.envelope) {
        await this.serial(async () => this.requireMls().completeLink(fromB64(link.envelope), linkId));
        await this.activateThisDevice();
        return;
      }
      await new Promise((resolve, reject) => {
        const timer = setTimeout(resolve, 2e3);
        this.pendingLink = {
          linkId,
          resolve: () => (clearTimeout(timer), resolve()),
          reject: (e) => (clearTimeout(timer), reject(e))
        };
      });
    }
    throw new Error("H\u1EBFt th\u1EDDi gian ch\u1EDD li\xEAn k\u1EBFt thi\u1EBFt b\u1ECB");
  }
  /** Thiết bị cũ: quét QR của thiết bị mới. Trả mã 6 số để người dùng đối chiếu trước khi gọi `approveLink`. */
  inspectLinkQr(qr) {
    const { l, k } = JSON.parse(qr);
    return { linkId: l, code: this.opts.crypto.linkCode(fromB64(k)) };
  }
  async approveLink(qr) {
    const { l, k } = JSON.parse(qr);
    const envelope = this.requireMls().approveLink(fromB64(k), l);
    await this.api.respondLink(l, toB64(envelope));
  }
  /** Ký DeviceList mới có thiết bị này (đã có USK) → server kích hoạt thiết bị. */
  async activateThisDevice() {
    await this.serial(async () => {
      const mls = this.requireMls();
      const current = await this.api.deviceList(this.session.user_id);
      let version = 1;
      let devices = [mls.ownDeviceEntry()];
      if (current) {
        const list = this.opts.crypto.verifyDeviceList(fromB64(current.payload), fromB64(current.signature), mls.uskPublicKey);
        version = list.version + 1;
        devices = [...list.devices.filter((d) => d.device_id !== mls.deviceId), mls.ownDeviceEntry()];
      }
      const signed = mls.signDeviceList(version, devices);
      await this.api.putDeviceList({ payload: toB64(signed.payload), signature: toB64(signed.signature) });
      await this.opts.store.setPinnedUsk(this.session.user_id, toB64(mls.uskPublicKey));
    });
    await this.becomeReady();
    await this.importArchive();
  }
  async becomeReady() {
    this.setStatus("ready");
    await this.start();
  }
  /** DeviceList đã kiểm chứng (chữ ký + USK ghim TOFU). */
  async verifiedDeviceList(userId, refresh = false) {
    if (!refresh && this.deviceLists.has(userId)) return this.deviceLists.get(userId);
    const res = await this.api.deviceList(userId);
    if (!res) return void 0;
    const pinned = await this.opts.store.getPinnedUsk(userId);
    const list = this.opts.crypto.verifyDeviceList(fromB64(res.payload), fromB64(res.signature), pinned ? fromB64(pinned) : void 0);
    if (list.user_id !== userId) throw new Error("DeviceList kh\xF4ng kh\u1EDBp ng\u01B0\u1EDDi d\xF9ng");
    if (!pinned) await this.opts.store.setPinnedUsk(userId, list.usk_pub);
    this.deviceLists.set(userId, list);
    return list;
  }
  async isTrustedMember(m) {
    try {
      const list = await this.verifiedDeviceList(m.user_id);
      if (list?.devices.some((d) => d.device_id === m.device_id && d.dsk_pub === m.signature_key)) return true;
      const fresh = await this.verifiedDeviceList(m.user_id, true);
      return !!fresh?.devices.some((d) => d.device_id === m.device_id && d.dsk_pub === m.signature_key);
    } catch {
      return false;
    }
  }
  /** Safety number với một người dùng (đối chiếu trực tiếp để chống MITM). */
  async safetyNumber(userId) {
    const mine = this.mls?.uskPublicKey;
    const theirs = await this.verifiedDeviceList(userId);
    if (!mine || !theirs || !this.session) return void 0;
    return this.opts.crypto.safetyNumber(this.session.user_id, mine, userId, fromB64(theirs.usk_pub));
  }
  // =====================================================================
  // Khởi động & đồng bộ
  // =====================================================================
  async start() {
    this.stopped = false;
    this.disposed = false;
    this.connectRealtime();
    await this.syncAll();
  }
  async shutdown() {
    this.stopped = true;
    this.disposed = true;
    this.prefsCache = void 0;
    this.prefsPending = void 0;
    clearTimeout(this.wsRetry);
    this.wsRetry = void 0;
    this.ws?.close();
    this.ws = void 0;
    for (const t of this.repairTimers.values()) clearTimeout(t);
    this.repairTimers.clear();
    for (const t of this.cleanupTimers.values()) clearTimeout(t);
    this.cleanupTimers.clear();
    for (const t of this.deliveredTimers.values()) clearTimeout(t);
    this.deliveredTimers.clear();
    for (const t of this.readRetryTimers.values()) clearTimeout(t);
    this.readRetryTimers.clear();
    this.readRetryAttempts.clear();
    this.summaryCache.clear();
    await this.chain;
  }
  async syncAll() {
    if (this.statusValue !== "ready") return;
    await this.loadUsers();
    await this.serial(async () => {
      await this.declareMlsFeatures();
      await this.processWelcomes();
      await this.refreshConversations();
      for (const id of this.conversations.keys()) await this.syncConversationInner(id);
      await this.replenishKeyPackages();
      await this.repairMembership();
      await this.joinMissingGroups();
      await this.publishGroupInfos();
    });
  }
  // =====================================================================
  // External Commit — thiết bị mới của thành viên tự vào nhóm (04 §2.4)
  // =====================================================================
  /**
   * Gửi External Commit. Server từ chối rõ ràng (4xx) → false. Lỗi mạng/5xx: server có thể đã nhận (mất phản hồi) →
   * thử lại cùng `client_msg_id` (server trả lại seq cũ); vẫn lỗi thì hỏi danh sách hội thoại xem thiết bị đã vào chưa.
   */
  async sendExternalJoin(conversationId, req) {
    for (let attempt = 0; attempt < 3; attempt++) {
      try {
        await this.api.externalJoin(conversationId, req);
        return true;
      } catch (e) {
        if (e instanceof SwChatApiError && e.status < 500 && e.status !== 408 && e.status !== 429) return false;
        this.log.warn(`external join ${conversationId}: ${e}`);
        await new Promise((r) => setTimeout(r, 500 * (attempt + 1)));
      }
    }
    const list = await this.api.conversations().catch(() => void 0);
    return !!list?.some((c) => c.id === conversationId && !c.left);
  }
  /** Lõi crypto xử lý được External Commit → báo server (một lần mỗi phiên). */
  async declareMlsFeatures() {
    if (this.mlsFeaturesDeclared || !this.requireMls().mergeExternalCommit) return;
    await this.api.setMlsFeatures({ external_commit: true }).then(() => this.mlsFeaturesDeclared = true).catch((e) => this.log.warn(`mls-features: ${e}`));
  }
  /** Đăng GroupInfo epoch hiện tại cho nhóm mà server đang giữ bản cũ / chưa có (lỗi bỏ qua — lần đồng bộ sau thử lại). */
  async publishGroupInfos(only) {
    const mls = this.requireMls();
    if (!mls.exportGroupInfo) return;
    for (const conv of this.conversations.values()) {
      if (conv.left || only && conv.id !== only) continue;
      const groupId = fromB64(conv.mls_group_id);
      let epoch;
      try {
        epoch = mls.epoch(groupId);
      } catch {
        continue;
      }
      if (epoch !== conv.epoch || conv.group_info_epoch === epoch) continue;
      try {
        await this.api.putGroupInfo(conv.id, { epoch, body: toB64(mls.exportGroupInfo(groupId)) });
        conv.group_info_epoch = epoch;
      } catch (e) {
        this.log.warn(`group-info ${conv.id}: ${e}`);
      }
    }
  }
  /**
   * Nhóm mình là thành viên mà thiết bị này chưa ở trong (đăng nhập lại / thiết bị mới, không ai thêm hộ): tự vào bằng
   * External Commit từ GroupInfo của server. Thành viên trong cây phải nằm trong DeviceList đã ký (như khi vào bằng
   * Welcome); server từ chối (đã có Welcome, epoch đổi, còn thiết bị cũ chưa hỗ trợ…) thì bỏ trạng thái và chờ lần sau.
   */
  async joinMissingGroups() {
    const mls = this.requireMls();
    if (!mls.joinByExternalCommit || !mls.discardGroup || !mls.mergeExternalCommit) return;
    const all = await this.api.allConversations().catch(() => []);
    const missing = all.filter((c) => !this.conversations.has(c.id) && c.group_info_epoch !== null && c.group_info_epoch === c.epoch);
    let joined = false;
    for (const conv of missing) {
      const groupId = fromB64(conv.mls_group_id);
      let info;
      try {
        info = await this.api.groupInfo(conv.id);
      } catch {
        continue;
      }
      let out;
      try {
        out = mls.joinByExternalCommit(groupId, fromB64(info.body));
      } catch (e) {
        this.log.warn(`external join ${conv.id}: ${e instanceof Error ? e.message : e}`);
        continue;
      }
      let trusted = true;
      for (const m of out.join.members) if (!await this.isTrustedMember(m)) trusted = false;
      if (!trusted) {
        mls.discardGroup(groupId);
        this.log.warn(`external join ${conv.id}: nh\xF3m c\xF3 thi\u1EBFt b\u1ECB kh\xF4ng n\u1EB1m trong DeviceList \u0111\xE3 k\xFD`);
        continue;
      }
      const req = { epoch: out.commit.epoch, body: toB64(out.commit.commit), client_msg_id: uuidv4() };
      if (await this.sendExternalJoin(conv.id, req)) {
        mls.commitAccepted(groupId);
        joined = true;
      } else {
        mls.discardGroup(groupId);
      }
    }
    if (joined) {
      await this.refreshConversations();
      for (const conv of missing) if (this.conversations.has(conv.id)) await this.syncConversationInner(conv.id);
    }
  }
  async loadUsers() {
    for (const u of await this.api.users()) this.users.set(u.id, u);
    const mine = this.session && this.users.get(this.session.user_id);
    if (mine && this.session && this.session.display_name !== mine.display_name) this.session.display_name = mine.display_name;
    this.emit("users");
  }
  /**
   * Đổi tên hiển thị / ảnh đại diện của mình (`display_name: null` quay về tên từ hệ thống đăng nhập; `avatar: null` bỏ ảnh).
   * Ảnh là WebP vuông đã nén (≤ 32 KB, base64url). Mọi client nhận sự kiện `profile` và tải lại danh bạ.
   */
  async updateProfile(req) {
    const updated = await this.api.updateProfile(req);
    this.users.set(updated.id, updated);
    if (this.session) this.session.display_name = updated.display_name;
    this.emit("users");
    this.emit("conversations");
    return updated;
  }
  getUsers() {
    return [...this.users.values()];
  }
  getUser(id) {
    return this.users.get(id);
  }
  /**
   * "Tất cả nhóm": mọi nhóm/kênh mình còn là thành viên theo server, kể cả cái thiết bị này chưa vào được (`synced: false`
   * — đăng nhập lại thành thiết bị mới, chờ thiết bị khác thêm vào). Tên nhóm mã hóa nên nhóm chưa đồng bộ hiện theo thành viên.
   */
  async listAllGroups() {
    const all = await this.api.allConversations();
    const out = [];
    for (const c of all) {
      if (c.kind === "direct") continue;
      const local = this.conversations.get(c.id);
      const names = c.members.filter((m) => m.user_id !== this.session?.user_id).map((m) => this.users.get(m.user_id)?.display_name ?? "?");
      const title = local ? this.titleOf(local, (await this.opts.store.getConversationState(c.id)).name) : `Nh\xF3m: ${names.slice(0, 3).join(", ")}${names.length > 3 ? "\u2026" : ""}`;
      out.push({ id: c.id, title, synced: !!local, member_count: c.members.length });
    }
    return out;
  }
  async refreshConversations() {
    const list = await this.api.conversations();
    this.conversations.clear();
    for (const c of list) this.conversations.set(c.id, c);
    for (const c of list) {
      const state = await this.opts.store.getConversationState(c.id);
      const roles = await this.roleChanges(c, state);
      if (!!state.left !== c.left || roles) {
        await this.opts.store.setConversationState(c.id, { ...state, left: c.left || void 0, ...roles && { roles } });
      }
      if (c.left) this.clearConversationTimers(c.id);
      await this.resolveLeaves(c);
    }
    if (list.some((c) => c.members.some((m) => !this.users.has(m.user_id)))) await this.loadUsers().catch(() => void 0);
    this.emit("conversations");
  }
  /**
   * W2.4: so vai trò thành viên server trả với lần trước (lưu trong trạng thái cục bộ) → ghi "đổi vai trò" (không biết
   * ai đổi — server không trả). Lần đầu thấy hội thoại chỉ ghi mốc. Trả bảng vai trò mới khi có thay đổi cần lưu.
   */
  async roleChanges(c, state) {
    if (c.kind === "direct" || c.left) return void 0;
    const now = Object.fromEntries(c.members.map((m) => [m.user_id, m.role]));
    const prev = state.roles;
    if (prev && Object.keys(now).length === Object.keys(prev).length && Object.entries(now).every(([u, r]) => prev[u] === r)) {
      return void 0;
    }
    if (prev) {
      const ts = Date.now();
      await this.addActivity(
        c.id,
        Object.entries(now).filter(([u, r]) => prev[u] !== void 0 && prev[u] !== r).map(([u, r]) => ({ id: `role:${u}:${r}:${ts}`, ts, seq: null, kind: "role_changed", subject: u, detail: r }))
      );
    }
    return now;
  }
  /**
   * W2.4: dấu "tự rời" (payload `leave`) của người đã không còn là thành viên theo server (mình: hội thoại `left`) →
   * ghi "đã rời" (id theo payload — không trùng) và xóa dấu. Người vẫn là thành viên (API rời lỗi) → giữ dấu, không ghi.
   * Dấu quá hạn (LEAVE_MARK_TTL_MS) bị bỏ, không ghi "đã rời" — người đó bị xóa sau này thì commit gỡ thiết bị ghi "bị
   * xóa". `justMarked`: người vừa gửi payload `leave` đang xử lý (dấu tính theo payload, kể cả khi đồng bộ bù tin cũ).
   */
  async resolveLeaves(c, justMarked) {
    const state = await this.opts.store.getConversationState(c.id);
    if (!state.leaving) return;
    const me = this.session?.user_id;
    const isGone = (u) => u === me ? c.left : !c.members.some((m) => m.user_id === u);
    const valid = (u, ts) => u === justMarked || Date.now() - ts <= LEAVE_MARK_TTL_MS;
    const done = Object.entries(state.leaving).filter(([u, l]) => isGone(u) || !valid(u, l.ts));
    if (!done.length) return;
    const leaving = { ...state.leaving };
    for (const [u] of done) delete leaving[u];
    await this.opts.store.setConversationState(c.id, { ...state, leaving: Object.keys(leaving).length ? leaving : void 0 });
    const gone = done.filter(([u, l]) => isGone(u) && valid(u, l.ts));
    await this.addActivity(
      c.id,
      gone.map(([u, l]) => ({ id: `leave:${l.id}`, ts: l.ts, seq: l.seq, kind: "member_left", actor: u, subject: u }))
    );
  }
  /** Hủy các việc hẹn giờ theo hội thoại (receipt, dọn thành viên) — khi mình đã rời. */
  clearConversationTimers(conversationId) {
    for (const timers of [this.deliveredTimers, this.cleanupTimers]) {
      clearTimeout(timers.get(conversationId));
      timers.delete(conversationId);
    }
    this.clearReadRetry(conversationId);
  }
  /**
   * Danh sách hội thoại, hoạt động mới nhất ở đầu. `projectId`: chỉ hội thoại gắn dự án đó (E2). Hội thoại mình đã
   * rời vẫn có trong danh sách với `left: true` (giữ lịch sử cục bộ).
   */
  async getConversations(opts = {}) {
    const views = [];
    for (const c of this.conversations.values()) {
      if (opts.projectId !== void 0 && c.project_id !== opts.projectId) continue;
      const gen = this.summaryGen.get(c.id) ?? 0;
      const state = await this.opts.store.getConversationState(c.id);
      let summary = this.summaryCache.get(c.id);
      if (!summary) {
        summary = await this.summarize(c, state);
        if ((this.summaryGen.get(c.id) ?? 0) === gen) this.summaryCache.set(c.id, summary);
      }
      views.push({ ...c, title: this.titleOf(c, state.name), avatar: this.avatarOfConversation(c, state.avatar), warning: state.warning, ...summary });
    }
    return views.sort((a, b) => b.last_activity_ts - a.last_activity_ts);
  }
  async summarize(c, state) {
    const me = this.session?.user_id;
    const visible = (await this.opts.store.listMessages(c.id)).filter((m) => VISIBLE_TYPES.has(m.type));
    const last = visible.at(-1);
    const readUpTo = state.read_up_to_seq ?? c.joined_seq;
    const unread = visible.filter((m) => m.sender_user_id !== me && m.seq !== null && m.seq > readUpTo && !m.deleted).length;
    return {
      last_message: last ? this.preview(last) : void 0,
      last_activity_ts: last?.ts ?? Date.parse(c.updated_at),
      unread_count: unread
    };
  }
  preview(m) {
    const text = m.deleted ? "Tin nh\u1EAFn \u0111\xE3 b\u1ECB x\xF3a" : m.type === "undecryptable" ? "Kh\xF4ng gi\u1EA3i m\xE3 \u0111\u01B0\u1EE3c tin nh\u1EAFn" : m.type === "file" ? fileLabel(m) : m.type === "poll" ? `\u{1F4CA} ${m.poll?.question ?? m.text ?? "B\xECnh ch\u1ECDn"}`.slice(0, 200) : m.type === "location" ? locationLabel(m) : m.type === "call" ? m.text ?? "Cu\u1ED9c g\u1ECDi" : (m.text ?? "").replace(/\s+/g, " ").slice(0, 200);
    return {
      id: m.id,
      sender_user_id: m.sender_user_id,
      text: m.thread_root && !m.deleted ? `${THREAD_PREVIEW_PREFIX}${text}` : text,
      ts: m.ts,
      type: m.type
    };
  }
  /** Tổng số tin chưa đọc trên mọi hội thoại (tiêu đề tab, badge portal) — không tính hội thoại đã rời. */
  async totalUnread() {
    return totalUnreadOf(await this.getConversations());
  }
  /** Ảnh của hội thoại: 1-1 = ảnh người kia; nhóm = ảnh trong `group_meta`. */
  avatarOfConversation(c, groupAvatar) {
    if (c.kind === "direct") {
      const other = c.members.find((m) => m.user_id !== this.session?.user_id);
      return other && this.users.get(other.user_id)?.avatar || void 0;
    }
    return groupAvatar;
  }
  titleOf(c, name) {
    if (c.kind === "direct") {
      const other = c.members.find((m) => m.user_id !== this.session?.user_id);
      return (other && this.users.get(other.user_id)?.display_name) ?? "Chat 1-1";
    }
    return name ?? "Nh\xF3m";
  }
  /**
   * `limit` tin mới nhất của dòng chính (không gồm tin trả lời trong luồng — W2.2; `threads: true` để lấy cả).
   * `includeId`: kéo dài về quá khứ tới (gồm) tin đó nếu nó cũ hơn — mở kết quả tìm kiếm / tin gốc của trả lời nằm
   * ngoài `limit` tin cuối.
   */
  async listMessages(conversationId, limit = 200, opts = {}) {
    const all = (await this.opts.store.listMessages(conversationId)).filter((m) => opts.threads || !m.thread_root);
    if (!opts.includeId) return all.slice(-limit);
    const at = all.findIndex((m) => m.id === opts.includeId);
    return all.slice(Math.max(0, Math.min(at < 0 ? Infinity : at, all.length - limit)));
  }
  /** W2.2: tin trả lời trong luồng của `rootId` (không gồm tin gốc), cũ nhất ở đầu. */
  async listThread(conversationId, rootId) {
    return (await this.opts.store.listMessages(conversationId)).filter((m) => m.thread_root === rootId);
  }
  /** W2.2: các luồng của hội thoại (số phản hồi, giờ phản hồi cuối), luồng mới hoạt động nhất ở đầu. */
  async threadSummaries(conversationId) {
    const byRoot = /* @__PURE__ */ new Map();
    for (const m of await this.opts.store.listMessages(conversationId)) {
      if (!m.thread_root || m.deleted || !VISIBLE_TYPES.has(m.type)) continue;
      let t = byRoot.get(m.thread_root);
      if (!t) byRoot.set(m.thread_root, t = { root_id: m.thread_root, reply_count: 0, last_ts: 0, participants: [] });
      t.reply_count++;
      t.last_ts = Math.max(t.last_ts, m.ts);
      if (!t.participants.includes(m.sender_user_id)) t.participants.push(m.sender_user_id);
    }
    return [...byRoot.values()].sort((a, b) => b.last_ts - a.last_ts);
  }
  /**
   * W2.3: tin file của hội thoại trong store cục bộ (gồm file gửi trong luồng, bỏ tin đã xóa), mới nhất ở đầu. Không
   * gọi server — tab "Tệp" chỉ liệt kê file thiết bị này đã giải mã (kể cả lấy qua archive); tải vẫn qua `downloadFile`.
   */
  async listFiles(conversationId) {
    const all = await this.opts.store.listMessages(conversationId);
    return all.filter((m) => m.type === "file" && m.file && !m.deleted).reverse();
  }
  /** W2.1: tin đang được ghim (bỏ tin đã xóa hoặc không có trên thiết bị), mới ghim nhất ở đầu. */
  async getPinned(conversationId) {
    const pins = (await this.opts.store.getConversationState(conversationId)).pins ?? {};
    const out = [];
    for (const [id, p] of Object.entries(pins)) {
      if (p.unpinned) continue;
      const message = await this.opts.store.getMessage(conversationId, id);
      if (!message || message.deleted) continue;
      out.push({ id, by: p.by, ts: p.ts, message });
    }
    return out.sort((a, b) => b.ts - a.ts);
  }
  /** W2.4: nhật ký hoạt động cục bộ của hội thoại, cũ nhất ở đầu. */
  getActivity(conversationId) {
    return this.opts.store.getActivity(conversationId);
  }
  /** Một tin trong store cục bộ (dựng trích dẫn khi trả lời, nguồn khi chuyển tiếp). */
  getMessage(conversationId, messageId) {
    return this.opts.store.getMessage(conversationId, messageId);
  }
  /** Tin có nhắc tới người dùng hiện tại. */
  mentionsMe(message) {
    return !!this.session && !!message.mentions?.includes(this.session.user_id);
  }
  /**
   * Tìm trong tin đã giải mã trên thiết bị này (không gửi gì lên server), không phân biệt hoa/thường và dấu
   * tiếng Việt. Mới nhất ở đầu.
   */
  async search(query, opts = {}) {
    const needle = normalizeSearch(query.trim());
    if (!needle) return [];
    const ids = opts.conversationId ? [opts.conversationId] : [...this.conversations.keys()];
    const results = [];
    for (const id of ids) {
      for (const m of await this.opts.store.listMessages(id)) {
        const haystack = m.type === "text" || m.type === "poll" || m.type === "location" ? m.text : m.type === "file" ? m.file?.name : void 0;
        if (m.deleted || !haystack) continue;
        const at = foldIndexOf(haystack, needle);
        if (at < 0) continue;
        results.push({
          conversation_id: id,
          message_id: m.id,
          seq: m.seq,
          ts: m.ts,
          sender_user_id: m.sender_user_id,
          snippet: m.type === "file" ? fileLabel(m) : snippetAround(haystack, at, needle.length)
        });
      }
    }
    results.sort((a, b) => b.ts - a.ts);
    return results.slice(0, opts.limit ?? 100);
  }
  syncConversation(conversationId) {
    return this.serial(() => this.syncConversationInner(conversationId));
  }
  async syncConversationInner(conversationId) {
    let conv = this.conversations.get(conversationId);
    if (!conv) {
      await this.refreshConversations();
      conv = this.conversations.get(conversationId);
      if (!conv) return;
    }
    const state = await this.opts.store.getConversationState(conversationId);
    let after = Math.max(state.cursor, conv.joined_seq);
    const archive = [];
    const incoming = [];
    let changed = false;
    for (; ; ) {
      let page;
      try {
        page = await this.api.messages(conversationId, after);
      } catch (e) {
        if (conv.left && e instanceof SwChatApiError && e.status === 403) break;
        throw e;
      }
      for (const m of page.messages) {
        const archived = await this.handleWire(conv, m, incoming);
        if (archived) archive.push({ seq: m.seq, plaintext: archived });
        after = m.seq;
        changed = true;
      }
      if (!page.has_more) break;
    }
    if (!changed) return;
    await this.opts.store.setConversationState(conversationId, { ...await this.opts.store.getConversationState(conversationId), cursor: after });
    await this.uploadArchive(conv, archive);
    await this.api.putCursors([{ conversation_id: conversationId, delivered_seq: after }]).catch(() => void 0);
    this.emit("messages", conversationId);
    if (incoming.length) {
      this.emit("incoming", conversationId, incoming);
      if (conv.kind === "direct" && !conv.left) this.scheduleDelivered(conversationId);
    }
  }
  /** Xử lý một tin từ server. Trả plaintext để đưa vào archive (nếu là tin ứng dụng hợp lệ). */
  async handleWire(conv, m, incoming) {
    const mls = this.requireMls();
    if (m.sender_device_id === this.session.device_id) return void 0;
    if (m.body === null) {
      await this.markDeletedBySeq(conv.id, [m.seq]);
      return void 0;
    }
    const before = m.kind === "commit" ? this.mlsMembers(conv) : void 0;
    let processed;
    try {
      processed = mls.process(fromB64(m.body));
    } catch (e) {
      if (m.kind === "application") {
        await this.opts.store.putMessages([
          {
            conversation_id: conv.id,
            id: `undecryptable:${m.seq}`,
            seq: m.seq,
            sender_user_id: m.sender_user_id,
            sender_device_id: m.sender_device_id,
            ts: Date.parse(m.created_at),
            type: "undecryptable",
            state: "sent"
          }
        ]);
      }
      this.log.warn(`Kh\xF4ng x\u1EED l\xFD \u0111\u01B0\u1EE3c tin ${conv.id}#${m.seq}: ${e instanceof Error ? e.message : e}`);
      return void 0;
    }
    if (processed.kind === "external_join") {
      const j = processed.joiner;
      const ok = !!mls.mergeExternalCommit && j.user_id === m.sender_user_id && j.device_id === m.sender_device_id && conv.members.some((x) => x.user_id === j.user_id) && await this.isTrustedMember(j);
      if (!ok) {
        await this.flag(conv.id, `T\u1EEB ch\u1ED1i thi\u1EBFt b\u1ECB ${j.device_id} c\u1EE7a ${j.user_id} t\u1EF1 v\xE0o nh\xF3m (kh\xF4ng kh\u1EDBp DeviceList \u0111\xE3 k\xFD)`);
        return void 0;
      }
      mls.mergeExternalCommit(fromB64(m.body), j);
      const after = this.mlsMembers(conv);
      if (before && after) await this.recordMembership(conv.id, j.user_id, before, after, m.seq, Date.parse(m.created_at));
      return void 0;
    }
    if (processed.kind === "commit") {
      for (const added of processed.added) {
        if (!await this.isTrustedMember(added)) {
          await this.flag(conv.id, `Thi\u1EBFt b\u1ECB ${added.device_id} c\u1EE7a ${added.user_id} kh\xF4ng n\u1EB1m trong DeviceList \u0111\xE3 k\xFD`);
        }
      }
      const after = this.mlsMembers(conv);
      if (before && after) await this.recordMembership(conv.id, processed.sender.user_id, before, after, m.seq, Date.parse(m.created_at));
      return void 0;
    }
    if (processed.kind !== "application") return void 0;
    const sender = processed.sender;
    if (sender.user_id !== m.sender_user_id || sender.device_id !== m.sender_device_id) {
      await this.flag(conv.id, `Server b\xE1o sai ng\u01B0\u1EDDi g\u1EEDi cho tin #${m.seq}`);
    }
    const payload = this.parsePayload(processed.plaintext);
    if (!payload) return void 0;
    const applied = await this.applyPayload(conv.id, payload, sender.user_id, sender.device_id, m.seq, trustedTs(payload.ts, m.created_at));
    if (!applied) return void 0;
    if (payload.type === "call" && Date.now() - trustedTs(payload.ts, m.created_at) < CALL_SIGNAL_MAX_AGE_MS) {
      this.emit("call", { conversation_id: conv.id, user_id: sender.user_id, device_id: sender.device_id, ts: payload.ts, body: payload.body });
    }
    if (sender.user_id !== this.session.user_id && (payload.type === "text" || payload.type === "file")) {
      const local = await this.opts.store.getMessage(conv.id, payload.id);
      if (local) incoming.push(local);
    }
    if (conv.secret || !this.shouldArchive(payload, sender.user_id)) return void 0;
    const archived = {
      sender_user_id: sender.user_id,
      sender_device_id: sender.device_id,
      created_at: m.created_at,
      payload
    };
    return utf8.encode(JSON.stringify(archived));
  }
  /**
   * Receipt không đưa vào archive (mỗi người một bản → nhân theo số thành viên), trừ receipt `read` của chính mình:
   * thiết bị mới cần nó để khôi phục `read_up_to_seq`.
   */
  shouldArchive(payload, senderUserId) {
    if (payload.type === "call") return payload.body.action === "end";
    if (payload.type !== "receipt") return true;
    return senderUserId === this.session.user_id && payload.body.kind === "read";
  }
  parsePayload(bytes) {
    try {
      const parsed = MessagePayload.safeParse(JSON.parse(utf8.decode(bytes)));
      return parsed.success ? parsed.data : void 0;
    } catch {
      return void 0;
    }
  }
  /**
   * Áp dụng payload vào kho cục bộ. Trả `false` khi payload bị từ chối (không đưa vào archive).
   * `fromArchive`: payload lấy từ archive — archive chỉ do thiết bị của chính mình ghi (khóa archive riêng của người
   * dùng), và chỉ chứa payload đã qua kiểm tra lúc xử lý trực tiếp → không kiểm lại vai trò theo danh sách thành viên
   * hiện tại (thiết bị mới có thể chưa tải hội thoại; người gửi có thể đã rời/bị hạ quyền sau khi gửi).
   */
  async applyPayload(conversationId, p, senderUserId, senderDeviceId, seq, ts, fromArchive = false) {
    const store = this.opts.store;
    switch (p.type) {
      case "text": {
        const existing = await store.getMessage(conversationId, p.id);
        if (existing && existing.sender_user_id !== senderUserId) {
          await this.flag(conversationId, `B\u1ECF qua tin tr\xF9ng id ${p.id} c\u1EE7a ng\u01B0\u1EDDi g\u1EEDi kh\xE1c (c\xF3 th\u1EC3 b\u1ECB gi\u1EA3 m\u1EA1o)`);
          break;
        }
        const edited = existing?.edited_at !== void 0;
        await store.putMessages([
          {
            conversation_id: conversationId,
            id: p.id,
            seq: existing?.seq ?? seq,
            sender_user_id: senderUserId,
            sender_device_id: existing?.sender_device_id ?? senderDeviceId,
            ts: existing?.ts ?? ts,
            type: "text",
            text: existing?.deleted ? void 0 : edited ? existing?.text : p.body.text,
            entities: existing?.deleted ? void 0 : edited ? existing?.entities : sanitizeEntities(p.body.text, p.body.entities),
            tags: edited ? existing?.tags : p.body.tags,
            mentions: edited ? existing?.mentions : p.body.mentions,
            reply_to: p.body.reply_to,
            forwarded_from: p.body.forwarded_from,
            thread_root: existing ? existing.thread_root : threadRootOf(p),
            reactions: existing?.reactions,
            edited_at: existing?.edited_at,
            deleted: existing?.deleted,
            state: "sent"
          }
        ]);
        break;
      }
      case "edit": {
        const target = await store.getMessage(conversationId, p.body.target);
        if (target && target.sender_user_id === senderUserId) {
          await store.putMessages([
            { ...target, text: p.body.text, entities: sanitizeEntities(p.body.text, p.body.entities), tags: p.body.tags, mentions: p.body.mentions, edited_at: ts }
          ]);
        }
        break;
      }
      case "important": {
        const target = await store.getMessage(conversationId, p.body.target);
        if (!target || target.deleted || target.sender_user_id !== senderUserId) break;
        if (target.important_ts !== void 0 && ts < target.important_ts) break;
        await store.putMessages([{ ...target, important: p.body.unmark ? void 0 : true, important_ts: ts }]);
        break;
      }
      case "delete":
        for (const id of p.body.targets) {
          const target = await store.getMessage(conversationId, id);
          if (target && target.sender_user_id === senderUserId) {
            await store.putMessages([
              { ...target, text: void 0, entities: void 0, file: void 0, poll: void 0, location: void 0, reactions: void 0, deleted: true }
            ]);
          }
        }
        break;
      case "group_meta": {
        if (!fromArchive) {
          const conv = this.conversations.get(conversationId);
          const role = conv?.members.find((m) => m.user_id === senderUserId)?.role;
          const serverVerified = seq !== null && conv?.meta_seq === seq;
          if (conv?.kind !== "direct" && role !== "owner" && role !== "admin" && !serverVerified) {
            this.log.warn(`B\u1ECF qua group_meta c\u1EE7a ${senderUserId} (kh\xF4ng ph\u1EA3i admin) trong ${conversationId}`);
            return false;
          }
        }
        const state = await store.getConversationState(conversationId);
        const incoming = p.body.avatar_data;
        const avatar = incoming === void 0 ? state.avatar : incoming === "" ? void 0 : isAvatarWebp(incoming) ? incoming : state.avatar;
        await store.setConversationState(conversationId, { ...state, name: p.body.name, avatar });
        if (state.name !== void 0 && state.name !== p.body.name) {
          await this.addActivity(conversationId, [
            { id: `meta:${p.id}`, ts, seq, kind: "renamed", actor: senderUserId, detail: p.body.name }
          ]);
        }
        this.emit("conversations");
        break;
      }
      case "pin": {
        if (!fromArchive) {
          const conv = this.conversations.get(conversationId);
          const role = conv?.members.find((m) => m.user_id === senderUserId)?.role;
          if (conv?.kind !== "direct" && role !== "owner" && role !== "admin") {
            this.log.warn(`B\u1ECF qua pin c\u1EE7a ${senderUserId} (kh\xF4ng ph\u1EA3i admin) trong ${conversationId}`);
            return false;
          }
        }
        const state = await store.getConversationState(conversationId);
        const prev = state.pins?.[p.body.target];
        if (prev && prev.seq !== null && seq !== null && seq < prev.seq) break;
        await store.setConversationState(conversationId, {
          ...state,
          pins: { ...state.pins, [p.body.target]: { by: senderUserId, ts, seq, unpinned: p.body.unpin || void 0 } }
        });
        await this.addActivity(conversationId, [
          { id: `pin:${p.id}`, ts, seq, kind: p.body.unpin ? "unpinned" : "pinned", actor: senderUserId, target: p.body.target }
        ]);
        break;
      }
      case "leave": {
        const conv = this.conversations.get(conversationId);
        if (conv?.kind === "direct") return false;
        const state = await store.getConversationState(conversationId);
        await store.setConversationState(conversationId, { ...state, leaving: { ...state.leaving, [senderUserId]: { id: p.id, ts, seq } } });
        if (conv) await this.resolveLeaves(conv, senderUserId);
        break;
      }
      case "reaction": {
        const target = await store.getMessage(conversationId, p.body.target);
        if (!target || target.deleted) break;
        const reactions = { ...target.reactions };
        const users = new Set(reactions[p.body.emoji] ?? []);
        if (p.body.remove) users.delete(senderUserId);
        else users.add(senderUserId);
        if (users.size) reactions[p.body.emoji] = [...users];
        else delete reactions[p.body.emoji];
        await store.putMessages([{ ...target, reactions }]);
        break;
      }
      case "receipt":
        await this.applyReceipt(conversationId, senderUserId, p.body.kind, p.body.up_to);
        break;
      case "file": {
        const existing = await store.getMessage(conversationId, p.id);
        if (existing && existing.sender_user_id !== senderUserId) {
          await this.flag(conversationId, `B\u1ECF qua tin tr\xF9ng id ${p.id} c\u1EE7a ng\u01B0\u1EDDi g\u1EEDi kh\xE1c (c\xF3 th\u1EC3 b\u1ECB gi\u1EA3 m\u1EA1o)`);
          break;
        }
        const b = p.body;
        const file = {
          file_id: b.file_id,
          key: b.key,
          prefix: b.prefix,
          sha256: b.sha256,
          size: b.size,
          mime: b.mime,
          name: b.name,
          width: b.width,
          height: b.height,
          duration: b.duration
        };
        await store.putMessages([
          {
            conversation_id: conversationId,
            id: p.id,
            seq: existing?.seq ?? seq,
            sender_user_id: senderUserId,
            sender_device_id: existing?.sender_device_id ?? senderDeviceId,
            ts: existing?.ts ?? ts,
            type: "file",
            text: existing?.deleted ? void 0 : b.caption,
            file: existing?.deleted ? void 0 : file,
            thread_root: existing ? existing.thread_root : threadRootOf(p),
            reactions: existing?.reactions,
            deleted: existing?.deleted,
            state: "sent"
          }
        ]);
        break;
      }
      case "poll":
      case "location": {
        const existing = await store.getMessage(conversationId, p.id);
        if (existing && existing.sender_user_id !== senderUserId) {
          await this.flag(conversationId, `B\u1ECF qua tin tr\xF9ng id ${p.id} c\u1EE7a ng\u01B0\u1EDDi g\u1EEDi kh\xE1c (c\xF3 th\u1EC3 b\u1ECB gi\u1EA3 m\u1EA1o)`);
          break;
        }
        const poll = p.type === "poll" ? { question: p.body.question, options: p.body.options, multiple: p.body.multiple, votes: existing?.poll?.votes ?? {} } : void 0;
        const location = p.type === "location" ? { ...p.body } : void 0;
        await store.putMessages([
          {
            conversation_id: conversationId,
            id: p.id,
            seq: existing?.seq ?? seq,
            sender_user_id: senderUserId,
            sender_device_id: existing?.sender_device_id ?? senderDeviceId,
            ts: existing?.ts ?? ts,
            type: p.type,
            text: existing?.deleted ? void 0 : p.type === "poll" ? p.body.question : [p.body.label, p.body.address].filter(Boolean).join(" \xB7 "),
            poll: existing?.deleted ? void 0 : poll,
            location: existing?.deleted ? void 0 : location,
            reactions: existing?.reactions,
            deleted: existing?.deleted,
            state: "sent"
          }
        ]);
        break;
      }
      case "call": {
        if (p.body.action !== "end" || !p.body.outcome) break;
        const existing = await store.getMessage(conversationId, p.id);
        if (existing && existing.sender_user_id !== senderUserId) break;
        const call = { call_id: p.body.call_id, video: !!p.body.video, outcome: p.body.outcome, duration: p.body.duration };
        await store.putMessages([
          {
            conversation_id: conversationId,
            id: p.id,
            seq: existing?.seq ?? seq,
            sender_user_id: senderUserId,
            sender_device_id: existing?.sender_device_id ?? senderDeviceId,
            ts: existing?.ts ?? ts,
            type: "call",
            text: callLabel(call),
            call,
            reactions: existing?.reactions,
            deleted: existing?.deleted,
            state: "sent"
          }
        ]);
        break;
      }
      case "vote": {
        const target = await store.getMessage(conversationId, p.body.target);
        if (!target?.poll || target.deleted) break;
        const poll = target.poll;
        const max = poll.multiple ? poll.options.length : 1;
        const options = [...new Set(p.body.options)];
        if (options.some((i) => i >= poll.options.length) || options.length > max) return false;
        const prev = poll.votes[senderUserId];
        if (prev && prev.seq !== null && seq !== null && seq < prev.seq) break;
        const votes = { ...poll.votes };
        if (options.length) votes[senderUserId] = { options, seq, ts };
        else delete votes[senderUserId];
        await store.putMessages([{ ...target, poll: { ...poll, votes } }]);
        break;
      }
      default:
        break;
    }
    return true;
  }
  /** Receipt của người khác → trạng thái đã nhận/đã xem; receipt `read` của chính mình (thiết bị khác) → đã đọc. */
  async applyReceipt(conversationId, senderUserId, kind, upTo) {
    const store = this.opts.store;
    const target = await store.getMessage(conversationId, upTo);
    if (!target || target.seq === null) return;
    const seq = target.seq;
    const state = await store.getConversationState(conversationId);
    if (senderUserId === this.session.user_id) {
      if (kind === "read" && seq > (state.read_sent_seq ?? state.read_up_to_seq ?? 0)) {
        await store.setConversationState(conversationId, {
          ...state,
          read_up_to_seq: Math.max(state.read_up_to_seq ?? 0, seq),
          read_sent_seq: seq
        });
      } else if (kind === "delivered" && seq > (state.delivered_sent_seq ?? 0)) {
        await store.setConversationState(conversationId, { ...state, delivered_sent_seq: seq });
      }
      return;
    }
    const prev = state.receipts?.[senderUserId] ?? { delivered_seq: 0, read_seq: 0 };
    const read_seq = kind === "read" ? Math.max(prev.read_seq, seq) : prev.read_seq;
    const delivered_seq = Math.max(prev.delivered_seq, seq, read_seq);
    if (read_seq === prev.read_seq && delivered_seq === prev.delivered_seq) return;
    await store.setConversationState(conversationId, {
      ...state,
      receipts: { ...state.receipts, [senderUserId]: { delivered_seq, read_seq } }
    });
  }
  async markDeletedBySeq(conversationId, seqs) {
    const all = await this.opts.store.listMessages(conversationId);
    const hits = all.filter((m) => m.seq !== null && seqs.includes(m.seq));
    if (hits.length) {
      await this.opts.store.putMessages(
        hits.map((m) => ({ ...m, text: void 0, entities: void 0, poll: void 0, location: void 0, reactions: void 0, deleted: true }))
      );
    }
  }
  async flag(conversationId, warning) {
    const state = await this.opts.store.getConversationState(conversationId);
    await this.opts.store.setConversationState(conversationId, { ...state, warning });
    this.emit("warning", conversationId, warning);
  }
  async processWelcomes() {
    const mls = this.requireMls();
    const pending = await this.api.welcomes();
    for (const w of pending) {
      try {
        const info = mls.joinFromWelcome(fromB64(w.body));
        for (const member of info.members) {
          if (!await this.isTrustedMember(member)) {
            await this.flag(w.conversation_id, `Th\xE0nh vi\xEAn ${member.user_id}/${member.device_id} kh\xF4ng n\u1EB1m trong DeviceList \u0111\xE3 k\xFD`);
          }
        }
      } catch (e) {
        this.log.warn(`Kh\xF4ng tham gia \u0111\u01B0\u1EE3c nh\xF3m ${w.conversation_id}: ${e instanceof Error ? e.message : e}`);
      }
      await this.api.ackWelcome(w.id);
    }
    if (pending.length) await this.refreshConversations();
  }
  async replenishKeyPackages() {
    const mls = this.requireMls();
    const { count, has_last_resort } = await this.api.keyPackageCount();
    if (count >= KEY_PACKAGE_LOW && has_last_resort) return;
    const fresh = count < KEY_PACKAGE_LOW ? mls.generateKeyPackages(KEY_PACKAGE_TARGET - count, false) : [];
    const lastResort = has_last_resort ? void 0 : mls.generateKeyPackages(1, true)[0];
    await this.api.uploadKeyPackages({
      packages: fresh.map(toB64),
      last_resort: lastResort ? toB64(lastResort) : void 0
    });
  }
  // =====================================================================
  // Thành viên nhóm
  // =====================================================================
  /**
   * Đảm bảo nhóm MLS khớp DeviceList: thêm thiết bị hợp lệ còn thiếu (của mình; hoặc của người khác nếu là
   * chat 1-1 / mình là admin) và xóa thiết bị đã bị loại khỏi DeviceList — 04 §2.4, §2.5.
   */
  async repairMembership(onlyUserId) {
    const mls = this.requireMls();
    const me = this.session.user_id;
    for (const conv of this.conversations.values()) {
      if (conv.left) continue;
      const myRole = conv.members.find((m) => m.user_id === me)?.role;
      const canManageOthers = conv.kind === "direct" || myRole === "owner" || myRole === "admin";
      const groupId = fromB64(conv.mls_group_id);
      let inGroup;
      try {
        inGroup = mls.members(groupId);
      } catch {
        continue;
      }
      const candidates = conv.members.map((m) => m.user_id).filter((u) => onlyUserId ? u === onlyUserId : true);
      const toAdd = [];
      const toRemove = [];
      for (const userId of candidates) {
        const list = await this.verifiedDeviceList(userId).catch(() => void 0);
        if (!list) continue;
        const present = inGroup.filter((m) => m.user_id === userId);
        if (list.devices.some((d) => !present.some((p) => p.device_id === d.device_id))) toAdd.push(userId);
        for (const p of canManageOthers || userId === me ? present : []) {
          if (!list.devices.some((d) => d.device_id === p.device_id && d.dsk_pub === p.signature_key)) {
            toRemove.push([p.user_id, p.device_id]);
          }
        }
      }
      const departed = onlyUserId ? [] : departedDevices(conv, inGroup);
      if (toRemove.length || departed.length) {
        await this.commitRemove(conv, toRemove, departed).catch((e) => this.log.warn(`remove: ${e}`));
      }
      if (toAdd.length) await this.addUserDevices(conv, toAdd).catch((e) => this.log.warn(`add: ${e}`));
    }
  }
  /**
   * Sau khi có người rời/bị xóa (sự kiện `members_changed`): gỡ thiết bị của họ khỏi nhóm MLS. Người rời không tự
   * commit được (server đã chặn) nên thành viên còn lại làm — admin/owner làm trước, member chờ lâu hơn để tránh tranh
   * epoch (ai commit trước thì những người sau thấy không còn gì để gỡ).
   */
  scheduleCleanup(conversationId) {
    const conv = this.conversations.get(conversationId);
    if (this.statusValue !== "ready" || !conv || conv.left || conv.kind === "direct") return;
    const role = conv.members.find((m) => m.user_id === this.session?.user_id)?.role;
    const delay = role === "owner" || role === "admin" ? 300 : 3e3 + Math.random() * 4e3;
    clearTimeout(this.cleanupTimers.get(conversationId));
    this.cleanupTimers.set(
      conversationId,
      setTimeout(() => {
        this.cleanupTimers.delete(conversationId);
        if (this.stopped) return;
        void this.serial(async () => {
          await this.refreshConversations();
          const fresh = this.conversations.get(conversationId);
          if (fresh && !fresh.left) await this.removeDepartedDevices(fresh);
        }).catch((e) => this.log.warn(`cleanup: ${e}`));
      }, delay)
    );
  }
  /**
   * Cây MLS (trạng thái cục bộ) không còn thiết bị nào của những người trong `userIds` đang KHÔNG là thành viên (người
   * được mời lại). Không đọc được cây (chưa vào nhóm) → `false`.
   */
  treeFreeOf(conv, userIds) {
    const active = new Set(conv.members.map((m) => m.user_id));
    const rejoining = new Set(userIds.filter((u) => !active.has(u)));
    try {
      return !this.requireMls().members(fromB64(conv.mls_group_id)).some((m) => rejoining.has(m.user_id));
    } catch {
      return false;
    }
  }
  /** Commit gỡ thiết bị của người không còn là thành viên (danh sách `conv` phải vừa tải lại). Gọi trong `serial`. */
  async removeDepartedDevices(conv) {
    let inGroup;
    try {
      inGroup = this.requireMls().members(fromB64(conv.mls_group_id));
    } catch {
      return;
    }
    const departed = departedDevices(conv, inGroup);
    if (departed.length) await this.commitRemove(conv, [], departed);
  }
  scheduleRepair(userId) {
    if (this.statusValue !== "ready") return;
    const delay = userId === this.session?.user_id ? 300 : 3e3 + Math.random() * 4e3;
    clearTimeout(this.repairTimers.get(userId));
    this.repairTimers.set(
      userId,
      setTimeout(() => {
        this.repairTimers.delete(userId);
        this.deviceLists.delete(userId);
        void this.serial(async () => {
          await this.refreshConversations();
          await this.repairMembership(userId);
        }).catch((e) => this.log.warn(`repair: ${e}`));
      }, delay)
    );
  }
  /**
   * Gửi commit với cơ chế thử lại khi xung đột epoch, và khi bị 429 (chờ `Retry-After` rồi dựng lại commit — không
   * thì `repairMembership` bỏ qua hội thoại đó tới lần sửa sau). Gọi bên trong `serial`. `prepare` (nếu có) chạy sau
   * mỗi lần đồng bộ, ngay trước `build` — để tải lại dữ liệu mà `build` dựa vào (vd. danh sách thành viên).
   */
  async commit(conv, build, prepare) {
    const mls = this.requireMls();
    const groupId = fromB64(conv.mls_group_id);
    let limited = 0;
    for (let attempt = 0; attempt < 4; ) {
      await this.syncConversationInner(conv.id);
      await prepare?.();
      const before = this.mlsMembers(conv);
      const built = build();
      if (!built) return;
      const { out, added, removed } = built;
      try {
        const { seq } = await this.api.send(conv.id, {
          kind: "commit",
          epoch: out.epoch,
          body: toB64(out.commit),
          client_msg_id: uuidv4(),
          notify: false,
          added_devices: added.length ? added : void 0,
          removed_devices: removed.length ? removed : void 0,
          welcome: out.welcome && added.length ? { body: toB64(out.welcome), device_ids: added } : void 0
        });
        mls.commitAccepted(groupId);
        if (mls.exportGroupInfo) {
          const epoch = mls.epoch(groupId);
          await this.api.putGroupInfo(conv.id, { epoch, body: toB64(mls.exportGroupInfo(groupId)) }).then(() => conv.group_info_epoch = epoch, (e) => this.log.warn(`group-info ${conv.id}: ${e}`));
        }
        const after = this.mlsMembers(conv);
        if (before && after) {
          await this.recordMembership(conv.id, this.session.user_id, before, after, seq, Date.now()).catch(
            (e) => this.log.warn(`activity: ${e}`)
          );
        }
        return;
      } catch (e) {
        mls.commitRejected(groupId);
        if (isRateLimited(e) && ++limited < RATE_RETRY_MAX) {
          await this.waitRetryAfter(e, `commit ${conv.id}`);
          continue;
        }
        if (!(e instanceof SwChatApiError && e.code === "epoch_conflict")) throw e;
        attempt++;
      }
    }
    throw new Error("Kh\xF4ng g\u1EEDi \u0111\u01B0\u1EE3c commit sau nhi\u1EC1u l\u1EA7n xung \u0111\u1ED9t epoch");
  }
  /** Lấy KeyPackage của các người dùng, chỉ giữ gói của thiết bị hợp lệ còn thiếu, rồi commit Add. */
  async addUserDevices(conv, userIds) {
    const mls = this.requireMls();
    const groupId = fromB64(conv.mls_group_id);
    const claimed = await this.api.claimKeyPackages(userIds);
    const verified = [];
    for (const c of claimed.packages) {
      const kp = fromB64(c.key_package);
      const owner = mls.inspectKeyPackage(kp);
      if (owner.user_id !== c.user_id || owner.device_id !== c.device_id || !await this.isTrustedMember(owner)) {
        this.log.warn(`B\u1ECF qua KeyPackage kh\xF4ng h\u1EE3p l\u1EC7 c\u1EE7a ${c.user_id}/${c.device_id}`);
        continue;
      }
      verified.push({ deviceId: c.device_id, kp });
    }
    await this.commit(conv, () => {
      const present = new Set(mls.members(groupId).map((m) => m.device_id));
      const missing = verified.filter((v) => !present.has(v.deviceId));
      if (!missing.length) return void 0;
      return {
        out: mls.addMembers(groupId, missing.map((m) => m.kp)),
        added: missing.map((m) => m.deviceId),
        removed: []
      };
    });
    await this.refreshConversations();
    await this.resendGroupMeta(conv);
  }
  /**
   * Commit gỡ thiết bị. `departed` là thiết bị gỡ vì chủ của chúng đã rời/bị xóa: danh sách này tính từ danh sách thành
   * viên lúc trước, nên mỗi lần dựng commit (sau khi đồng bộ) tải lại thành viên từ server và bỏ thiết bị của người nay
   * đã là thành viên — nếu không, người vừa được mời lại có thể bị gỡ mất thiết bị vừa thêm (cùng device_id) khi lần
   * dọn dẹp tranh epoch với commit Add. `devices` (thiết bị bị thu hồi, khóa lệch) gỡ không điều kiện.
   */
  async commitRemove(conv, devices, departed = []) {
    const mls = this.requireMls();
    const groupId = fromB64(conv.mls_group_id);
    let active;
    await this.commit(
      conv,
      () => {
        const present = new Set(mls.members(groupId).map((m) => m.device_id));
        const gone = active ? departed.filter(([u]) => !active.has(u)) : [];
        const still = [...devices, ...gone].filter(([, d], i, all) => present.has(d) && all.findIndex(([, x]) => x === d) === i);
        if (!still.length) return void 0;
        return { out: mls.removeDevices(groupId, still), added: [], removed: still.map(([, d]) => d) };
      },
      departed.length ? async () => {
        const fresh = await this.api.conversation(conv.id).catch(() => void 0);
        active = fresh && !fresh.left ? new Set(fresh.members.map((m) => m.user_id)) : void 0;
      } : void 0
    );
  }
  // =====================================================================
  // Tạo hội thoại & gửi tin
  // =====================================================================
  /** Mở (hoặc tạo) chat 1-1 với người dùng. */
  async openDirect(userId) {
    const existing = [...this.conversations.values()].find(
      (c) => c.kind === "direct" && c.members.some((m) => m.user_id === userId)
    );
    if (existing) return existing.id;
    return this.createConversation("direct", [userId]);
  }
  /**
   * Thiết bị này đã ở trong hội thoại (server liệt kê theo thiết bị). Chat 1-1 đã có từ trước nhưng thiết bị vừa khôi
   * phục/liên kết chưa được thêm vào nhóm MLS thì `openDirect` vẫn trả id (nhánh `duplicate`) mà chưa mở được.
   */
  hasConversation(conversationId) {
    return this.conversations.has(conversationId);
  }
  async createGroup(name, memberIds, projectId) {
    const id = await this.createConversation("group", memberIds, projectId, name);
    await this.sendPayload(id, { type: "group_meta", body: { name, project_id: projectId } }, true, true);
    return id;
  }
  // =====================================================================
  // Quản lý nhóm (B1, B2) — kiểm quyền ở client trước khi gọi; server kiểm lại
  // =====================================================================
  /** Vai trò của mình trong hội thoại (undefined nếu không còn là thành viên). */
  myRole(conversationId) {
    const conv = this.conversations.get(conversationId);
    return conv?.members.find((m) => m.user_id === this.session?.user_id)?.role;
  }
  /** Mình được thêm/xóa người khác, đổi tên, đổi vai trò trong nhóm này (admin/owner, chưa rời). */
  canManage(conversationId) {
    const conv = this.conversations.get(conversationId);
    const role = this.myRole(conversationId);
    return !!conv && conv.kind !== "direct" && !conv.left && (role === "owner" || role === "admin");
  }
  requireManage(conversationId) {
    const conv = this.conversations.get(conversationId);
    if (!conv) throw new Error("Kh\xF4ng t\xECm th\u1EA5y h\u1ED9i tho\u1EA1i");
    if (conv.kind === "direct") throw new Error("Chat 1-1 kh\xF4ng qu\u1EA3n l\xFD th\xE0nh vi\xEAn \u0111\u01B0\u1EE3c");
    if (conv.left) throw new Error("B\u1EA1n \u0111\xE3 r\u1EDDi h\u1ED9i tho\u1EA1i n\xE0y");
    if (!this.canManage(conversationId)) throw new Error("Ch\u1EC9 admin c\u1EE7a nh\xF3m \u0111\u01B0\u1EE3c l\xE0m vi\u1EC7c n\xE0y");
    return conv;
  }
  /** Thêm người vào nhóm có sẵn: mời (server) rồi commit Add thiết bị của họ kèm Welcome, gửi lại tên nhóm. */
  async addMembers(conversationId, userIds) {
    this.requireManage(conversationId);
    const ids = [...new Set(userIds)].filter((u) => u !== this.session?.user_id);
    if (!ids.length) return;
    await this.serial(async () => {
      await this.refreshConversations();
      const before = this.conversations.get(conversationId);
      if (before && !before.left) await this.removeDepartedDevices(before);
      await this.api.invite(conversationId, ids, before !== void 0 && this.treeFreeOf(before, ids));
      await this.refreshConversations();
      const conv = this.conversations.get(conversationId);
      if (conv) await this.addUserDevices(conv, ids);
    });
  }
  /**
   * Xóa thành viên (admin/owner; không xóa được owner). Server chặn người đó đọc tin mới ngay khi gọi API, sau đó
   * commit gỡ thiết bị của họ khỏi nhóm MLS (lỗi commit chỉ ghi log — thành viên khác sẽ dọn khi nhận `members_changed`).
   */
  async removeMember(conversationId, userId) {
    if (userId === this.session?.user_id) return this.leaveGroup(conversationId);
    const conv = this.requireManage(conversationId);
    const target = conv.members.find((m) => m.user_id === userId);
    if (!target) throw new Error("Ng\u01B0\u1EDDi n\xE0y kh\xF4ng ph\u1EA3i th\xE0nh vi\xEAn nh\xF3m");
    if (target.role === "owner") throw new Error("Kh\xF4ng th\u1EC3 x\xF3a owner c\u1EE7a nh\xF3m");
    await this.serial(async () => {
      await this.api.removeMember(conversationId, userId);
      await this.refreshConversations();
      const fresh = this.conversations.get(conversationId);
      if (fresh) await this.removeDepartedDevices(fresh).catch((e) => this.log.warn(`remove member: ${e}`));
    });
    this.emit("messages", conversationId);
  }
  /**
   * Rời nhóm. Thiết bị của mình được thành viên còn lại gỡ khỏi nhóm MLS; lịch sử đã giải mã vẫn giữ trên máy,
   * hội thoại chuyển sang `left: true` (không gửi/nhận tin mới, không tính vào số chưa đọc).
   */
  async leaveGroup(conversationId) {
    const conv = this.conversations.get(conversationId);
    if (!conv) throw new Error("Kh\xF4ng t\xECm th\u1EA5y h\u1ED9i tho\u1EA1i");
    if (conv.kind === "direct") throw new Error("Kh\xF4ng th\u1EC3 r\u1EDDi chat 1-1");
    if (conv.left) return;
    await this.serial(async () => {
      await this.sendPayloadInner(conversationId, { type: "leave", body: {} }, false).catch((e) => this.log.warn(`leave: ${e}`));
      try {
        await this.api.removeMember(conversationId, this.session.user_id);
      } catch (e) {
        const state = await this.opts.store.getConversationState(conversationId);
        if (state.leaving?.[this.session.user_id]) {
          const rest = { ...state.leaving };
          delete rest[this.session.user_id];
          await this.opts.store.setConversationState(conversationId, { ...state, leaving: Object.keys(rest).length ? rest : void 0 });
        }
        throw e;
      }
      await this.refreshConversations();
    });
    this.emit("messages", conversationId);
  }
  /**
   * Xóa nhóm — chỉ chủ nhóm (server trả 403 với người khác). Xóa hẳn trên server (tin, file, thành viên); mọi thành viên
   * nhận sự kiện `conv_deleted` và nhóm biến khỏi danh sách. Không hoàn tác được.
   */
  async deleteGroup(conversationId) {
    const conv = this.conversations.get(conversationId);
    if (!conv) throw new Error("Kh\xF4ng t\xECm th\u1EA5y h\u1ED9i tho\u1EA1i");
    if (conv.kind === "direct") throw new Error("Kh\xF4ng th\u1EC3 x\xF3a chat 1-1");
    await this.api.deleteConversation(conversationId);
    this.clearConversationTimers(conversationId);
    await this.serial(() => this.refreshConversations());
  }
  /**
   * Xóa hội thoại khỏi danh sách của MÌNH — chat 1-1, hoặc nhóm đã rời (nhóm còn tham gia: rời nhóm trước, hoặc chủ nhóm
   * xóa nhóm). Không ảnh hưởng người khác. Xóa lịch sử trên máy này, lưu trữ (archive) của mình trên server và trên mọi thiết
   * bị khác của mình (nhận sự kiện `conv_deleted`). Chat 1-1 có tin mới thì hiện lại, chỉ gồm tin mới.
   */
  async hideConversation(conversationId) {
    const conv = this.conversations.get(conversationId);
    if (!conv) throw new Error("Kh\xF4ng t\xECm th\u1EA5y h\u1ED9i tho\u1EA1i");
    if (conv.kind !== "direct" && !conv.left) throw new Error("H\xE3y r\u1EDDi nh\xF3m tr\u01B0\u1EDBc khi x\xF3a h\u1ED9i tho\u1EA1i");
    await this.api.hideConversation(conversationId);
    await this.serial(async () => {
      await this.purgeLocalConversation(conversationId);
      await this.refreshConversations();
    });
    this.emit("messages", conversationId);
  }
  /** Dọn dữ liệu cục bộ của hội thoại đã xóa (tin, hoạt động, ghim…); giữ mốc đồng bộ để không tải lại tin cũ. */
  async purgeLocalConversation(conversationId) {
    const state = await this.opts.store.getConversationState(conversationId);
    await this.opts.store.deleteConversationData?.(conversationId);
    await this.opts.store.setConversationState(conversationId, { cursor: state.cursor, archive_cursor: state.archive_cursor, left: state.left });
    this.clearConversationTimers(conversationId);
  }
  /** Đổi tên nhóm: `group_meta` mã hóa, kèm cờ `meta` để server kiểm quyền admin (B2). */
  async renameGroup(conversationId, name) {
    const conv = this.requireManage(conversationId);
    const trimmed = name.trim();
    if (!trimmed) throw new Error("T\xEAn nh\xF3m kh\xF4ng \u0111\u01B0\u1EE3c tr\u1ED1ng");
    await this.sendPayload(
      conversationId,
      { type: "group_meta", body: { name: trimmed, project_id: conv.project_id ?? void 0 } },
      false,
      true
    );
  }
  /** Đổi ảnh nhóm (admin/owner): `group_meta` mã hóa mang ảnh WebP nhỏ; `null` = bỏ ảnh. Tên nhóm giữ nguyên. */
  async setGroupAvatar(conversationId, avatar) {
    const conv = this.requireManage(conversationId);
    if (avatar !== null && !isAvatarWebp(avatar)) throw new Error("\u1EA2nh nh\xF3m ph\u1EA3i l\xE0 WebP \u2264 32 KB");
    const state = await this.opts.store.getConversationState(conversationId);
    const name = state.name?.trim();
    if (!name) throw new Error("Nh\xF3m ch\u01B0a c\xF3 t\xEAn");
    await this.sendPayload(
      conversationId,
      { type: "group_meta", body: { name, avatar_data: avatar ?? "", project_id: conv.project_id ?? void 0 } },
      false,
      true
    );
  }
  /** Đổi vai trò admin ↔ member (admin/owner; không đổi được vai trò owner). */
  async setRole(conversationId, userId, role) {
    const conv = this.requireManage(conversationId);
    const target = conv.members.find((m) => m.user_id === userId);
    if (!target) throw new Error("Ng\u01B0\u1EDDi n\xE0y kh\xF4ng ph\u1EA3i th\xE0nh vi\xEAn nh\xF3m");
    if (target.role === "owner") throw new Error("Kh\xF4ng th\u1EC3 \u0111\u1ED5i vai tr\xF2 c\u1EE7a owner");
    await this.serial(async () => {
      await this.api.updateRole(conversationId, userId, role);
      const ts = Date.now();
      await this.addActivity(conversationId, [
        { id: `role:${userId}:${role}:${ts}`, ts, seq: null, kind: "role_changed", actor: this.session.user_id, subject: userId, detail: role }
      ]);
      const state = await this.opts.store.getConversationState(conversationId);
      if (state.roles) await this.opts.store.setConversationState(conversationId, { ...state, roles: { ...state.roles, [userId]: role } });
      await this.refreshConversations();
    });
  }
  // =====================================================================
  // Ghim tin (W2.1)
  // =====================================================================
  /** Mình được ghim/bỏ ghim trong hội thoại này: 1-1 cả hai người; nhóm owner/admin; chưa rời. */
  canPin(conversationId) {
    const conv = this.conversations.get(conversationId);
    if (!conv || conv.left) return false;
    const role = this.myRole(conversationId);
    return conv.kind === "direct" || role === "owner" || role === "admin";
  }
  /** Ghim (hoặc bỏ ghim khi `unpin`) một tin: payload `pin` mã hóa — server không biết tin nào được ghim. */
  async pin(conversationId, messageId, unpin = false) {
    if (!this.canPin(conversationId)) throw new Error("Ch\u1EC9 admin c\u1EE7a nh\xF3m \u0111\u01B0\u1EE3c ghim tin");
    if (!unpin) {
      const target = await this.opts.store.getMessage(conversationId, messageId);
      if (!target || target.deleted || target.type !== "text" && target.type !== "file") throw new Error("Kh\xF4ng ghim \u0111\u01B0\u1EE3c tin n\xE0y");
    }
    await this.sendPayload(conversationId, { type: "pin", body: { target: messageId, unpin: unpin || void 0 } }, false);
  }
  // =====================================================================
  // Hoạt động (W2.4) — nhật ký cục bộ, không lưu trên server
  // =====================================================================
  /** Thành viên hiện có trong cây MLS của hội thoại theo trạng thái cục bộ; chưa vào nhóm → undefined. */
  mlsMembers(conv) {
    try {
      return this.requireMls().members(fromB64(conv.mls_group_id));
    } catch {
      return void 0;
    }
  }
  /**
   * So thành viên MLS trước/sau một commit (gộp theo người): người mới có thiết bị → "đã thêm"; người không còn thiết
   * bị nào → "đã xóa" — trừ khi sự kiện thành viên gần nhất của họ là "đã rời" (payload `leave`). Thêm/gỡ thiết bị của
   * người vẫn là thành viên (liên kết/thu hồi thiết bị) không phải hoạt động thành viên.
   */
  async recordMembership(conversationId, actor, before, after, seq, ts) {
    const had = new Set(before.map((m) => m.user_id));
    const has = new Set(after.map((m) => m.user_id));
    const items = [];
    const added = [...has].filter((u) => !had.has(u));
    for (const u of added) items.push({ id: `commit:${seq}:add:${u}`, ts, seq, kind: "member_added", actor, subject: u });
    const gone = [...had].filter((u) => !has.has(u));
    const state = await this.opts.store.getConversationState(conversationId);
    const leaving = { ...state.leaving };
    let marksChanged = false;
    for (const u of added) marksChanged = delete leaving[u] || marksChanged;
    if (gone.length) {
      const log = await this.opts.store.getActivity(conversationId);
      for (const u of gone) {
        const mark = leaving[u];
        if (mark && ts - mark.ts > LEAVE_MARK_TTL_MS) {
          delete leaving[u];
          marksChanged = true;
        } else if (mark) {
          items.push({ id: `leave:${mark.id}`, ts: mark.ts, seq: mark.seq, kind: "member_left", actor: u, subject: u });
          delete leaving[u];
          marksChanged = true;
          continue;
        }
        const last = log.findLast((a) => a.subject === u && MEMBERSHIP_KINDS.has(a.kind));
        if (last?.kind === "member_left") continue;
        items.push({ id: `commit:${seq}:remove:${u}`, ts, seq, kind: "member_removed", actor, subject: u });
      }
    }
    if (marksChanged) {
      await this.opts.store.setConversationState(conversationId, { ...state, leaving: Object.keys(leaving).length ? leaving : void 0 });
    }
    await this.addActivity(conversationId, items);
  }
  /** Thêm sự kiện vào nhật ký (bỏ trùng theo id, sắp theo thời gian, giữ ACTIVITY_LIMIT mục mới nhất). Gọi trong `serial`. */
  async addActivity(conversationId, items) {
    if (!items.length) return;
    const store = this.opts.store;
    const list = await store.getActivity(conversationId);
    const ids = new Set(list.map((a) => a.id));
    const fresh = items.filter((a) => !ids.has(a.id));
    if (!fresh.length) return;
    await store.setActivity(conversationId, [...list, ...fresh].sort((a, b) => a.ts - b.ts).slice(-ACTIVITY_LIMIT));
    this.emit("activity", conversationId);
  }
  // =====================================================================
  // Dự án (E1, E2)
  // =====================================================================
  /** Dự án của mình (server đồng bộ từ portal khi đăng nhập). `refresh`: tải lại thay vì dùng bộ đệm. */
  async getProjects(refresh = false) {
    if (this.projects && !refresh) return this.projects;
    const gen = this.projectsGen;
    const list = await this.api.projects();
    if (gen === this.projectsGen) this.projects = list;
    return list;
  }
  clearProjects() {
    this.projects = void 0;
    this.projectsGen++;
  }
  /** User id thành viên dự án đã dùng SW Chat (gợi ý khi tạo nhóm dự án). */
  projectMembers(projectId) {
    return this.api.projectMembers(projectId);
  }
  // =====================================================================
  // Thông báo đẩy (Q4) — payload chỉ báo "có tin mới" + id hội thoại, không có nội dung
  // =====================================================================
  /** Khóa công khai VAPID (base64url) cho `pushManager.subscribe`; `undefined` khi server chưa bật push. */
  async pushPublicKey() {
    try {
      return (await this.api.vapidKey()).public_key;
    } catch (e) {
      if (e instanceof SwChatApiError && e.code === "push_disabled") return void 0;
      throw e;
    }
  }
  /**
   * Đăng ký Web Push cho thiết bị này: `subscription` là `PushSubscription.toJSON()` của trình duyệt (endpoint +
   * khóa p256dh/auth). Server chỉ gửi push khi thiết bị không kết nối WebSocket. Gọi lại thì thay subscription cũ.
   */
  async enablePush(subscription) {
    const { endpoint, keys } = subscription;
    if (!endpoint || !keys?.p256dh || !keys.auth) throw new Error("Subscription thi\u1EBFu endpoint ho\u1EB7c kh\xF3a p256dh/auth");
    const req = {
      endpoint,
      expiration_time: subscription.expirationTime ?? null,
      keys: { p256dh: keys.p256dh, auth: keys.auth }
    };
    await this.api.putPushSubscription(req);
  }
  /** App mobile: đăng ký token FCM của thiết bị (server chỉ gửi push "có tin mới", không nội dung, khi thiết bị không có WebSocket). */
  async enableFcmPush(token) {
    await this.api.putFcmToken(token);
  }
  async disableFcmPush() {
    await this.api.deleteFcmToken();
  }
  /** Gỡ subscription của thiết bị này trên server (trình duyệt tự `unsubscribe()` phía mình). */
  async disablePush() {
    await this.api.deletePushSubscription();
  }
  async createConversation(kind, memberIds, projectId, name) {
    return this.serial(async () => {
      const mls = this.requireMls();
      const groupId = mls.createGroup();
      let conv;
      try {
        conv = await this.api.createConversation({
          kind,
          mls_group_id: toB64(groupId),
          members: memberIds,
          project_id: projectId,
          secret: false
        });
      } catch (e) {
        if (e instanceof SwChatApiError && e.code === "duplicate") {
          await this.refreshConversations();
          return e.message;
        }
        throw e;
      }
      this.conversations.set(conv.id, conv);
      if (name) await this.opts.store.setConversationState(conv.id, { ...await this.opts.store.getConversationState(conv.id), name });
      await this.addUserDevices(conv, [.../* @__PURE__ */ new Set([...memberIds, this.session.user_id])]);
      return conv.id;
    });
  }
  /**
   * Người mới chỉ đọc được từ epoch được thêm vào → gửi lại tên nhóm sau mỗi lần Add. Chỉ admin/owner gửi (người khác
   * bỏ qua `group_meta` của member); thiết bị mới của member lấy tên nhóm từ archive.
   */
  async resendGroupMeta(conv) {
    if (conv.kind === "direct" || !this.canManage(conv.id)) return;
    const state = await this.opts.store.getConversationState(conv.id);
    if (state.name) {
      await this.retryRateLimited(
        `group_meta ${conv.id}`,
        () => this.sendPayloadInner(
          conv.id,
          { type: "group_meta", body: { name: state.name, avatar_data: state.avatar, project_id: conv.project_id ?? void 0 } },
          false,
          true
        )
      );
    }
  }
  /**
   * Gửi tin văn bản. `mentions` là user id được nhắc; bỏ trống thì tự nhận diện `@Tên hiển thị` của thành viên
   * trong nội dung. Chỉ giữ người là thành viên hội thoại (trừ chính mình).
   */
  async sendText(conversationId, text, opts = {}) {
    const tags = [.../* @__PURE__ */ new Set([...[...text.matchAll(TAG_RE)].map((m) => m[1]), ...opts.tags ?? []])];
    const threadRoot = await this.resolveThreadRoot(conversationId, opts.threadRoot);
    return this.sendPayload(conversationId, {
      type: "text",
      body: {
        text,
        entities: sanitizeEntities(text, opts.entities),
        mentions: this.resolveMentions(conversationId, text, opts.mentions),
        tags,
        reply_to: opts.replyTo,
        thread_root: threadRoot
      }
    });
  }
  /** W2.2: luồng chỉ một cấp — trả lời trong luồng của một tin đã nằm trong luồng thì dùng tin gốc của luồng đó. */
  async resolveThreadRoot(conversationId, rootId) {
    if (!rootId) return void 0;
    const root = await this.opts.store.getMessage(conversationId, rootId);
    if (!root || root.deleted || root.type !== "text" && root.type !== "file") throw new Error("Kh\xF4ng t\xECm th\u1EA5y tin g\u1ED1c c\u1EE7a lu\u1ED3ng");
    return root.thread_root ?? root.id;
  }
  async editText(conversationId, messageId, text, opts = {}) {
    const tags = [...new Set([...text.matchAll(TAG_RE)].map((m) => m[1]))];
    const mentions = this.resolveMentions(conversationId, text, opts.mentions);
    await this.sendPayload(conversationId, { type: "edit", body: { target: messageId, text, entities: sanitizeEntities(text, opts.entities), mentions, tags } }, false);
  }
  resolveMentions(conversationId, text, explicit) {
    const conv = this.conversations.get(conversationId);
    if (!conv) return [];
    const others = conv.members.map((m) => m.user_id).filter((u) => u !== this.session?.user_id);
    if (conv.kind !== "direct" && hasAllMention(text)) return others;
    if (explicit) return [...new Set(explicit)].filter((u) => others.includes(u));
    const named = others.flatMap((id) => {
      const name = this.users.get(id)?.display_name;
      return name ? [{ id, name }] : [];
    });
    return detectMentions(text, named);
  }
  /** Chuyển tiếp một tin văn bản sang hội thoại khác (kèm `forwarded_from` trỏ về nguồn gốc). */
  async forwardMessage(sourceConversationId, messageId, targetConversationId) {
    const src = await this.opts.store.getMessage(sourceConversationId, messageId);
    if (!src || src.type !== "text" || src.deleted || src.text === void 0) {
      throw new Error("Ch\u1EC9 chuy\u1EC3n ti\u1EBFp \u0111\u01B0\u1EE3c tin v\u0103n b\u1EA3n");
    }
    if (this.conversations.get(sourceConversationId)?.secret) throw new Error("Kh\xF4ng chuy\u1EC3n ti\u1EBFp tin t\u1EEB h\u1ED9i tho\u1EA1i b\xED m\u1EADt");
    const origin = src.forwarded_from ?? { conversation_id: sourceConversationId, sender: src.sender_user_id };
    return this.sendPayload(targetConversationId, {
      type: "text",
      body: { text: src.text, entities: src.entities, mentions: [], tags: src.tags ?? [], forwarded_from: origin }
    });
  }
  /** Tạo bình chọn: 2–12 lựa chọn không trống và không trùng; `multiple` cho phép chọn nhiều. */
  async sendPoll(conversationId, question, options, multiple = false) {
    const q = question.trim();
    const opts = options.map((o) => o.trim()).filter(Boolean);
    if (!q) throw new Error("C\u1EA7n nh\u1EADp c\xE2u h\u1ECFi");
    if (opts.length < 2 || opts.length > 12) throw new Error("B\xECnh ch\u1ECDn c\u1EA7n t\u1EEB 2 \u0111\u1EBFn 12 l\u1EF1a ch\u1ECDn");
    if (new Set(opts).size !== opts.length) throw new Error("C\xE1c l\u1EF1a ch\u1ECDn kh\xF4ng \u0111\u01B0\u1EE3c tr\xF9ng nhau");
    return this.sendPayload(conversationId, { type: "poll", body: { question: q.slice(0, 512), options: opts.map((o) => o.slice(0, 256)), multiple } });
  }
  /** Bỏ phiếu (thay phiếu cũ của mình); `optionIndexes` rỗng = rút phiếu. */
  async sendVote(conversationId, pollId, optionIndexes) {
    const poll = await this.opts.store.getMessage(conversationId, pollId);
    if (!poll?.poll || poll.deleted) throw new Error("Kh\xF4ng t\xECm th\u1EA5y b\xECnh ch\u1ECDn");
    const picked = [...new Set(optionIndexes)];
    if (picked.some((i) => !Number.isInteger(i) || i < 0 || i >= poll.poll.options.length)) throw new Error("L\u1EF1a ch\u1ECDn kh\xF4ng h\u1EE3p l\u1EC7");
    if (picked.length > (poll.poll.multiple ? poll.poll.options.length : 1)) throw new Error("B\xECnh ch\u1ECDn n\xE0y ch\u1EC9 cho ch\u1ECDn m\u1ED9t \u0111\xE1p \xE1n");
    await this.sendPayload(conversationId, { type: "vote", body: { target: pollId, options: picked } }, false);
  }
  /**
   * Gửi tín hiệu cuộc gọi 1-1 (kênh MLS). `invite` đẩy thông báo (không nội dung); các tín hiệu khác im lặng; `end` (kèm
   * `outcome`) là dòng lịch sử cuộc gọi hiện trong hội thoại.
   */
  async sendCallSignal(conversationId, body) {
    await this.sendPayload(conversationId, { type: "call", body }, body.action === "invite" || body.action === "end");
  }
  /** Danh sách STUN/TURN để dựng `RTCPeerConnection` (TURN dùng tài khoản tạm do server cấp). */
  async getIceServers() {
    return (await this.api.iceServers()).ice_servers;
  }
  /** Gửi vị trí (mã hóa E2EE như mọi tin). Không gọi dịch vụ bản đồ nào. */
  async sendLocation(conversationId, loc) {
    return this.sendPayload(conversationId, {
      type: "location",
      body: {
        lat: loc.lat,
        lng: loc.lng,
        accuracy: loc.accuracy,
        label: loc.label?.trim().slice(0, 256) || void 0,
        address: loc.address?.trim().slice(0, 512) || void 0
      }
    });
  }
  /**
   * Gửi file (C1): mã hóa từng khối 1 MiB trên máy (STREAM AES-256-GCM, khóa ngẫu nhiên cho mỗi file), tải ciphertext
   * lên theo khối, rồi gửi payload `file` (tên, mime, khóa — chỉ trong MLS) kèm `file_ids` để server ghi tham chiếu.
   * Chỉ giữ một khối plaintext/ciphertext trong bộ nhớ mỗi lần.
   */
  async sendFile(conversationId, opts) {
    const conv = this.conversations.get(conversationId);
    if (!conv) throw new Error("Kh\xF4ng t\xECm th\u1EA5y h\u1ED9i tho\u1EA1i");
    if (conv.left) throw new Error("B\u1EA1n \u0111\xE3 r\u1EDDi h\u1ED9i tho\u1EA1i n\xE0y");
    const size = opts.data instanceof Uint8Array ? opts.data.length : opts.data.size;
    if (size > MAX_FILE_SIZE) throw new Error("File v\u01B0\u1EE3t qu\xE1 gi\u1EDBi h\u1EA1n 100 MB");
    const threadRoot = await this.resolveThreadRoot(conversationId, opts.threadRoot);
    const crypto2 = this.opts.crypto;
    const { key, prefix } = crypto2.newFileKey();
    const count = Math.max(1, Math.ceil(size / FILE_CHUNK_SIZE));
    const { file_id } = await this.api.createFile(conversationId, size + count * FILE_TAG_SIZE);
    const hasher = crypto2.createFileHasher();
    let sha256;
    try {
      opts.onProgress?.(0);
      for (let i = 0; i < count; i++) {
        const start = i * FILE_CHUNK_SIZE;
        const end = Math.min(start + FILE_CHUNK_SIZE, size);
        const plain = opts.data instanceof Uint8Array ? opts.data.subarray(start, end) : new Uint8Array(await opts.data.slice(start, end).arrayBuffer());
        const ct = crypto2.encryptFileChunk(key, prefix, file_id, i, i === count - 1, plain);
        hasher.update(ct);
        await this.putChunkWithRetry(file_id, i, ct);
        opts.onProgress?.((i + 1) / count);
      }
      sha256 = hasher.finalize();
    } finally {
      hasher.free?.();
    }
    await this.api.completeFile(file_id, toB64(sha256));
    return this.serial(
      () => this.sendPayloadInner(
        conversationId,
        {
          type: "file",
          body: {
            file_id,
            key: toB64(key),
            prefix: toB64(prefix),
            sha256: toB64(sha256),
            size,
            mime: opts.mime || "application/octet-stream",
            name: opts.name.slice(0, 255),
            width: opts.width,
            height: opts.height,
            duration: opts.duration,
            thread_root: threadRoot
          }
        },
        true,
        false,
        [file_id]
      )
    );
  }
  /** F3: khối bị 429 → chờ `Retry-After` rồi gửi lại khối đó (có giới hạn số lần); lỗi khác ném ngay. */
  putChunkWithRetry(fileId, index, ct) {
    return this.retryRateLimited(`upload kh\u1ED1i ${index}`, () => this.api.putFileChunk(fileId, index, ct));
  }
  /** F3: `fn` bị 429 → chờ `Retry-After` rồi gọi lại (tối đa `RATE_RETRY_MAX` lần); lỗi khác ném ngay. */
  async retryRateLimited(label, fn) {
    for (let attempt = 1; ; attempt++) {
      try {
        return await fn();
      } catch (e) {
        if (!isRateLimited(e) || attempt >= RATE_RETRY_MAX) throw e;
        await this.waitRetryAfter(e, label);
      }
    }
  }
  async waitRetryAfter(e, label) {
    const sec = Math.min(e.retryAfter ?? RATE_RETRY_DEFAULT_SEC, RATE_RETRY_MAX_SEC);
    this.log.warn(`${label} b\u1ECB gi\u1EDBi h\u1EA1n t\u1EA7n su\u1EA5t, th\u1EED l\u1EA1i sau ${sec}s`);
    await new Promise((r) => setTimeout(r, sec * 1e3));
  }
  /**
   * Gửi lại tin văn bản/file của mình đã gửi lỗi (`state: 'failed'`, vd. 429 `rate_limited`): cùng id payload, cùng
   * nội dung (file đã tải lên trước đó được tham chiếu lại, không tải lại). Lỗi lần này → lại `failed` và ném lỗi.
   */
  async resendMessage(conversationId, messageId) {
    return this.serial(async () => {
      const m = await this.opts.store.getMessage(conversationId, messageId);
      if (!m || m.state !== "failed" || m.deleted || m.sender_user_id !== this.session?.user_id) {
        throw new Error("Tin n\xE0y kh\xF4ng c\u1EA7n g\u1EEDi l\u1EA1i");
      }
      if (m.type === "text" && m.text !== void 0) {
        return this.sendPayloadInner(conversationId, {
          id: m.id,
          type: "text",
          body: {
            text: m.text,
            entities: m.entities,
            mentions: m.mentions ?? [],
            tags: m.tags ?? [],
            reply_to: m.reply_to,
            forwarded_from: m.forwarded_from,
            thread_root: m.thread_root
          }
        });
      }
      if (m.type === "file" && m.file) {
        const f = m.file;
        return this.sendPayloadInner(
          conversationId,
          {
            id: m.id,
            type: "file",
            body: {
              file_id: f.file_id,
              key: f.key,
              prefix: f.prefix,
              sha256: f.sha256,
              size: f.size,
              mime: f.mime,
              name: f.name,
              width: f.width,
              height: f.height,
              caption: m.text,
              thread_root: m.thread_root
            }
          },
          true,
          false,
          [f.file_id]
        );
      }
      if (m.type === "poll" && m.poll) {
        const { question, options, multiple } = m.poll;
        return this.sendPayloadInner(conversationId, { id: m.id, type: "poll", body: { question, options, multiple } });
      }
      if (m.type === "location" && m.location) {
        return this.sendPayloadInner(conversationId, { id: m.id, type: "location", body: { ...m.location } });
      }
      throw new Error("Ch\u1EC9 g\u1EEDi l\u1EA1i \u0111\u01B0\u1EE3c tin v\u0103n b\u1EA3n, file, b\xECnh ch\u1ECDn ho\u1EB7c v\u1ECB tr\xED");
    });
  }
  /**
   * Tải và giải mã file của một tin `file`: kiểm sha256 của ciphertext rồi giải mã từng khối (sai khóa, đảo/cắt khối
   * → lỗi). Người đã rời/bị xóa khỏi hội thoại nhận lỗi 404 (`SwChatApiError`).
   */
  async downloadFile(message, opts = {}) {
    const file = message.file;
    if (!file) throw new Error("Tin kh\xF4ng c\xF3 file");
    const crypto2 = this.opts.crypto;
    const ct = await this.api.downloadFile(file.file_id);
    const hasher = crypto2.createFileHasher();
    let digest;
    try {
      const HASH_SLICE = 1024 * 1024;
      for (let off = 0; off < ct.length; off += HASH_SLICE) hasher.update(ct.subarray(off, off + HASH_SLICE));
      digest = hasher.finalize();
    } finally {
      hasher.free?.();
    }
    if (toB64(digest) !== file.sha256) throw new Error("File b\u1ECB thay \u0111\u1ED5i (sha256 kh\xF4ng kh\u1EDBp)");
    const encChunk = FILE_CHUNK_SIZE + FILE_TAG_SIZE;
    const count = Math.max(1, Math.ceil(ct.length / encChunk));
    if (ct.length - count * FILE_TAG_SIZE !== file.size) throw new Error("K\xEDch th\u01B0\u1EDBc file kh\xF4ng kh\u1EDBp");
    const key = fromB64(file.key);
    const prefix = fromB64(file.prefix);
    const out = new Uint8Array(file.size);
    for (let i = 0; i < count; i++) {
      const plain = crypto2.decryptFileChunk(key, prefix, file.file_id, i, i === count - 1, ct.subarray(i * encChunk, (i + 1) * encChunk));
      out.set(plain, i * FILE_CHUNK_SIZE);
      opts.onProgress?.((i + 1) / count);
    }
    return out;
  }
  /**
   * Thả (hoặc bỏ khi `remove`) emoji lên một tin. Không tạo thông báo. Bị giới hạn tần suất (429) → bỏ qua lần này:
   * reaction trên máy được trả lại như cũ, không ném lỗi (giả định 20).
   */
  async react(conversationId, targetId, emoji, remove = false) {
    const target = await this.opts.store.getMessage(conversationId, targetId);
    if (!target || target.deleted || !VISIBLE_TYPES.has(target.type) || target.type === "undecryptable") {
      throw new Error("Kh\xF4ng th\u1EA3 reaction \u0111\u01B0\u1EE3c cho tin n\xE0y");
    }
    try {
      await this.sendPayload(
        conversationId,
        { type: "reaction", body: { target: targetId, emoji, remove: remove || void 0 } },
        false
      );
    } catch (e) {
      if (!isRateLimited(e)) throw e;
      this.log.warn("reaction b\u1ECB gi\u1EDBi h\u1EA1n t\u1EA7n su\u1EA5t \u2014 b\u1ECF qua");
    }
  }
  /** Đánh dấu / bỏ đánh dấu "quan trọng" cho tin của chính mình (người khác không đánh dấu được tin của bạn). */
  async setImportant(conversationId, targetId, important) {
    const target = await this.opts.store.getMessage(conversationId, targetId);
    if (!target || target.deleted || target.sender_user_id !== this.session?.user_id || !VISIBLE_TYPES.has(target.type) || target.type === "undecryptable") {
      throw new Error("Ch\u1EC9 \u0111\xE1nh d\u1EA5u quan tr\u1ECDng \u0111\u01B0\u1EE3c tin do ch\xEDnh b\u1EA1n g\u1EEDi");
    }
    await this.sendPayload(conversationId, { type: "important", body: { target: targetId, unmark: important ? void 0 : true } }, false);
  }
  /**
   * Đánh dấu đã đọc tới tin mới nhất: cập nhật `read_up_to_seq` và gửi receipt `read` (mã hóa) nếu có tin mới
   * của người khác. Gọi khi người dùng đang mở hội thoại và tab đang hiển thị.
   *
   * Chỉ phát sự kiện khi `read_up_to_seq` thực sự đổi (số chưa đọc đổi) — receipt gửi lỗi không phát sự kiện, nếu
   * không UI tải lại danh sách → gọi markRead lại → vòng lặp gửi liên tục. Receipt lỗi được gửi lại có backoff.
   */
  markRead(conversationId) {
    return this.serial(async () => {
      const store = this.opts.store;
      const withSeq = (await store.listMessages(conversationId)).filter((m) => m.seq !== null);
      const maxSeq = withSeq.at(-1)?.seq ?? 0;
      const state = await store.getConversationState(conversationId);
      const prev = state.read_up_to_seq ?? 0;
      if (maxSeq > prev) {
        await store.setConversationState(conversationId, {
          ...state,
          read_up_to_seq: maxSeq,
          read_sent_seq: state.read_sent_seq ?? prev
        });
      }
      await this.flushReadReceipt(conversationId);
      if (maxSeq > prev) {
        this.emit("messages", conversationId);
        this.emit("conversations");
      }
    });
  }
  /**
   * Gửi receipt `read` cho phần đã đọc trên máy này nhưng chưa gửi được (`read_sent_seq < read_up_to_seq`).
   * Lỗi tạm thời (mạng, server) → hẹn gửi lại với backoff; 403 (vd. thành viên không được đăng trong kênh) → bỏ
   * qua hẳn, không thử lại. Chạy trong `serial`.
   */
  async flushReadReceipt(conversationId) {
    const store = this.opts.store;
    const state = await store.getConversationState(conversationId);
    const upTo = state.read_up_to_seq ?? 0;
    const sent = state.read_sent_seq ?? upTo;
    if (sent >= upTo) return this.clearReadRetry(conversationId);
    if (this.conversations.get(conversationId)?.left) {
      await store.setConversationState(conversationId, { ...state, read_sent_seq: upTo });
      return this.clearReadRetry(conversationId);
    }
    const me = this.session.user_id;
    const withSeq = (await store.listMessages(conversationId)).filter((m) => m.seq !== null && m.seq <= upTo);
    const newFromOthers = withSeq.some((m) => m.sender_user_id !== me && m.seq > sent);
    const target = withSeq.findLast((m) => UUID_RE.test(m.id));
    if (newFromOthers && target) {
      try {
        await this.sendPayloadInner(conversationId, { type: "receipt", body: { kind: "read", up_to: target.id } }, false);
      } catch (e) {
        this.log.warn(`receipt read: ${e}`);
        if (isRateLimited(e)) {
          const latest2 = await store.getConversationState(conversationId);
          await store.setConversationState(conversationId, { ...latest2, read_sent_seq: sent });
          return this.clearReadRetry(conversationId);
        }
        if (!isPermanentApiError(e)) {
          const latest2 = await store.getConversationState(conversationId);
          await store.setConversationState(conversationId, { ...latest2, read_sent_seq: sent });
          return this.scheduleReadRetry(conversationId);
        }
      }
    }
    const latest = await store.getConversationState(conversationId);
    await store.setConversationState(conversationId, {
      ...latest,
      read_sent_seq: Math.max(latest.read_sent_seq ?? 0, upTo)
    });
    this.clearReadRetry(conversationId);
  }
  scheduleReadRetry(conversationId) {
    if (this.stopped || this.readRetryTimers.has(conversationId)) return;
    const attempt = (this.readRetryAttempts.get(conversationId) ?? 0) + 1;
    this.readRetryAttempts.set(conversationId, attempt);
    const delay = Math.min(READ_RETRY_BASE_MS * 2 ** (attempt - 1), READ_RETRY_MAX_MS);
    this.readRetryTimers.set(
      conversationId,
      setTimeout(() => {
        this.readRetryTimers.delete(conversationId);
        if (this.stopped) return;
        if (this.statusValue !== "ready") return this.scheduleReadRetry(conversationId);
        void this.serial(() => this.flushReadReceipt(conversationId)).catch((e) => this.log.warn(`receipt read: ${e}`));
      }, delay)
    );
  }
  clearReadRetry(conversationId) {
    clearTimeout(this.readRetryTimers.get(conversationId));
    this.readRetryTimers.delete(conversationId);
    this.readRetryAttempts.delete(conversationId);
  }
  /**
   * Ai đã nhận/đã xem một tin (theo người). 1-1: `read.length > 0` là "đã xem"; nhóm: số người đã xem. Chỉ tính
   * thành viên đang hoạt động — người đã rời/bị xóa không còn được đếm.
   */
  async readStatus(conversationId, messageId) {
    const msg = await this.opts.store.getMessage(conversationId, messageId);
    return readStatusOf(msg, await this.receipts(conversationId), this.session?.user_id);
  }
  /**
   * Receipt của từng thành viên đang hoạt động (UI tự tính trạng thái cho nhiều tin bằng `readStatusOf`). Receipt cũ
   * của người đã rời vẫn lưu trên máy (được mời lại thì dùng lại) nhưng không trả về.
   */
  async receipts(conversationId) {
    const all = (await this.opts.store.getConversationState(conversationId)).receipts ?? {};
    const conv = this.conversations.get(conversationId);
    if (!conv) return all;
    const active = new Set(conv.members.map((m) => m.user_id));
    return Object.fromEntries(Object.entries(all).filter(([userId]) => active.has(userId)));
  }
  /**
   * Gửi receipt `delivered` sau khi nhận tin mới trong chat 1-1 (gom trong DELIVERED_DEBOUNCE_MS). Thiết bị khác của
   * mình đã gửi tới seq đó (thấy qua đồng bộ) thì bỏ qua — mỗi người thường chỉ một receipt.
   */
  scheduleDelivered(conversationId) {
    if (this.stopped) return;
    clearTimeout(this.deliveredTimers.get(conversationId));
    this.deliveredTimers.set(
      conversationId,
      setTimeout(() => {
        this.deliveredTimers.delete(conversationId);
        if (this.stopped || this.statusValue !== "ready") return;
        void this.serial(() => this.sendDeliveredInner(conversationId)).catch((e) => this.log.warn(`receipt delivered: ${e}`));
      }, DELIVERED_DEBOUNCE_MS)
    );
  }
  async sendDeliveredInner(conversationId) {
    if (this.conversations.get(conversationId)?.left) return;
    await this.syncConversationInner(conversationId);
    const store = this.opts.store;
    const me = this.session.user_id;
    const latest = (await store.listMessages(conversationId)).findLast(
      (m) => m.sender_user_id !== me && m.seq !== null && UUID_RE.test(m.id)
    );
    if (!latest?.seq) return;
    const state = await store.getConversationState(conversationId);
    if (latest.seq <= Math.max(state.delivered_sent_seq ?? 0, state.read_up_to_seq ?? 0)) return;
    await this.sendPayloadInner(conversationId, { type: "receipt", body: { kind: "delivered", up_to: latest.id } }, false);
    const after = await store.getConversationState(conversationId);
    await store.setConversationState(conversationId, { ...after, delivered_sent_seq: latest.seq });
  }
  /** Xóa cho mọi người: gửi tin `delete` (client khác ẩn) + yêu cầu server xóa ciphertext. */
  async deleteMessage(conversationId, messageId) {
    const target = await this.opts.store.getMessage(conversationId, messageId);
    await this.sendPayload(conversationId, { type: "delete", body: { targets: [messageId] } }, false);
    if (target?.seq) await this.api.deleteMessages(conversationId, [target.seq]);
  }
  sendTyping(conversationId) {
    if (this.ws?.readyState === 1) this.ws.send(JSON.stringify({ t: "typing", conversation_id: conversationId }));
  }
  /** `meta`: tin `group_meta` — gửi kèm cờ để server chỉ cho admin/owner (B2). */
  sendPayload(conversationId, partial, notify = true, meta = false) {
    return this.serial(() => this.sendPayloadInner(conversationId, partial, notify, meta));
  }
  async sendPayloadInner(conversationId, partial, notify = true, meta = false, fileIds) {
    const mls = this.requireMls();
    const conv = this.conversations.get(conversationId);
    if (!conv) throw new Error("Kh\xF4ng t\xECm th\u1EA5y h\u1ED9i tho\u1EA1i");
    if (conv.left) throw new Error("B\u1EA1n \u0111\xE3 r\u1EDDi h\u1ED9i tho\u1EA1i n\xE0y");
    const payload = MessagePayload.parse({ v: 1, id: uuidv7(), ts: Date.now(), ...partial });
    const me = this.session;
    const groupId = fromB64(conv.mls_group_id);
    const reactionTarget = payload.type === "reaction" || payload.type === "vote" ? await this.opts.store.getMessage(conversationId, payload.body.target) : void 0;
    const accepted = await this.applyPayload(conversationId, payload, me.user_id, me.device_id, null, payload.ts);
    if (payload.type === "text" || payload.type === "file" || payload.type === "poll" || payload.type === "location") {
      const local = await this.opts.store.getMessage(conversationId, payload.id);
      await this.opts.store.putMessages([{ ...local, state: "pending" }]);
    }
    const silent = payload.type === "receipt" || payload.type === "call" && payload.body.action !== "end";
    if (!silent) this.emit("messages", conversationId);
    const plaintext = utf8.encode(JSON.stringify(payload));
    try {
      let seq;
      for (let attempt = 0; ; attempt++) {
        const wire = mls.encrypt(groupId, plaintext);
        const sentEpoch = mls.epoch(groupId);
        try {
          ({ seq } = await this.api.send(conversationId, {
            kind: "application",
            epoch: sentEpoch,
            body: toB64(wire),
            client_msg_id: payload.id,
            notify,
            expires_at: payload.type === "receipt" ? new Date(Date.now() + RECEIPT_TTL_MS).toISOString() : payload.type === "call" && payload.body.action !== "end" ? new Date(Date.now() + CALL_SIGNAL_TTL_MS).toISOString() : void 0,
            meta: meta || void 0,
            file_ids: fileIds?.length ? fileIds : void 0
          }));
          break;
        } catch (e) {
          if (attempt >= 3 || !(e instanceof SwChatApiError && e.code === "epoch_conflict")) throw e;
          await this.syncConversationInner(conversationId);
          if (mls.epoch(groupId) <= sentEpoch) {
            throw new Error("Thi\u1EBFt b\u1ECB n\xE0y l\u1EC7ch epoch v\u1EDBi nh\xF3m (kh\xF4ng x\u1EED l\xFD \u0111\u01B0\u1EE3c commit) \u2014 c\u1EA7n \u0111\u01B0\u1EE3c th\xEAm l\u1EA1i v\xE0o h\u1ED9i tho\u1EA1i");
          }
        }
      }
      const local = await this.opts.store.getMessage(conversationId, payload.id);
      if (local) await this.opts.store.putMessages([{ ...local, seq, state: "sent" }]);
      if (payload.type === "vote" && accepted) {
        const poll = await this.opts.store.getMessage(conversationId, payload.body.target);
        const mine = poll?.poll?.votes[me.user_id];
        if (poll?.poll && mine && mine.seq === null) {
          await this.opts.store.putMessages([{ ...poll, poll: { ...poll.poll, votes: { ...poll.poll.votes, [me.user_id]: { ...mine, seq } } } }]);
        }
      }
      if (payload.type === "pin" && accepted) await this.pinSent(conversationId, payload.body.target, payload.ts, seq, !!payload.body.unpin);
      if (accepted && !conv.secret && this.shouldArchive(payload, me.user_id)) {
        const archived = {
          sender_user_id: me.user_id,
          sender_device_id: me.device_id,
          created_at: new Date(payload.ts).toISOString(),
          payload
        };
        await this.uploadArchive(conv, [{ seq, plaintext: utf8.encode(JSON.stringify(archived)) }]);
      }
      if (!silent) this.emit("messages", conversationId);
      return await this.opts.store.getMessage(conversationId, payload.id) ?? this.failedPlaceholder(conversationId, payload);
    } catch (e) {
      const local = await this.opts.store.getMessage(conversationId, payload.id);
      if (local) await this.opts.store.putMessages([{ ...local, state: "failed" }]);
      if (reactionTarget) {
        const current = await this.opts.store.getMessage(conversationId, reactionTarget.id);
        if (current) await this.opts.store.putMessages([{ ...current, reactions: reactionTarget.reactions, poll: reactionTarget.poll }]);
      }
      if (!silent) this.emit("messages", conversationId);
      throw e;
    }
  }
  /**
   * W2.1: ghim của mình đã có seq → áp lại theo thứ tự seq như payload nhận được. Trong lúc chờ (vd. thử lại sau
   * `epoch_conflict`), payload ghim/bỏ ghim của người khác có thể đã ghi đè trạng thái chờ của mình; nếu payload đó có seq
   * nhỏ hơn thì ghim của mình (seq lớn hơn — các thiết bị khác cũng áp nó sau) phải thắng, nếu không thiết bị gửi sẽ lệch.
   */
  async pinSent(conversationId, target, ts, seq, unpin) {
    const state = await this.opts.store.getConversationState(conversationId);
    const p = state.pins?.[target];
    if (p && p.seq !== null && p.seq > seq) return;
    await this.opts.store.setConversationState(conversationId, {
      ...state,
      pins: { ...state.pins, [target]: { by: this.session.user_id, ts, seq, unpinned: unpin || void 0 } }
    });
  }
  failedPlaceholder(conversationId, payload) {
    return {
      conversation_id: conversationId,
      id: payload.id,
      seq: null,
      sender_user_id: this.session.user_id,
      sender_device_id: this.session.device_id,
      ts: payload.ts,
      type: payload.type,
      state: "sent"
    };
  }
  // =====================================================================
  // Thông báo công ty & tùy chọn cá nhân
  // =====================================================================
  announcements = (before, limit) => this.api.announcements(before, limit);
  createAnnouncement = (req) => this.api.createAnnouncement(req);
  /**
   * Tải một ảnh / tệp lên để đính kèm thông báo sắp gửi (≤ 200 MB, theo khối 1 MB). Trả id để đưa vào `attachment_ids`.
   * Tệp không gắn thông báo nào trong 24 giờ bị máy chủ dọn.
   */
  async uploadAnnouncementAttachment(file, name, onProgress) {
    const mime = file.type || "application/octet-stream";
    const { id } = await this.api.createAnnouncementAttachment(name, mime, file.size);
    const count = announcementChunkCount(file.size);
    for (let i = 0; i < count; i++) {
      const chunk = new Uint8Array(await file.slice(i * ANNOUNCEMENT_CHUNK_BYTES, (i + 1) * ANNOUNCEMENT_CHUNK_BYTES).arrayBuffer());
      await this.api.putAnnouncementChunk(id, i, chunk);
      onProgress?.((i + 1) / count);
    }
    await this.api.completeAnnouncementAttachment(id);
    return id;
  }
  /** Nội dung ảnh / tệp đính kèm thông báo (để hiển thị hoặc tải xuống). */
  announcementAttachment = (id) => this.api.downloadAnnouncementAttachment(id);
  sentAnnouncements = () => this.api.sentAnnouncements();
  markAnnouncementsRead = (ids) => this.api.markAnnouncementsRead(ids);
  ackAnnouncement = (id) => this.api.ackAnnouncement(id);
  reactAnnouncement = (id, reaction) => this.api.setAnnouncementReaction(id, reaction);
  libraryInstance;
  /** Lưu trữ cá nhân của người dùng (mã hóa bằng khóa lưu trữ; xem `library.ts`). */
  get library() {
    return this.libraryInstance ??= new PersonalLibrary(this.api, this.opts.crypto, () => this.requireMls());
  }
  /** Cache khối tùy chọn đã giải mã (version của server) + bản đang ghi dở (lạc quan) để giao diện phản hồi ngay. */
  prefsCache;
  prefsPending;
  prefsQueue = Promise.resolve();
  /** Tùy chọn hiện tại (đồng bộ; mặc định rỗng tới khi `loadPrefs` xong). */
  get prefs() {
    return this.prefsPending ?? this.prefsCache?.prefs ?? emptyPrefs();
  }
  /**
   * Khối tùy chọn được mã hóa bằng khóa lưu trữ (cùng khóa với archive, chia sẻ giữa các thiết bị của người dùng); địa chỉ
   * (conversation_id giả "~prefs", seq = version) ràng buộc khối với đúng phiên bản của nó.
   */
  openPrefs(r) {
    if (!r.blob) return { version: 0, prefs: emptyPrefs() };
    const json = JSON.parse(utf8.decode(this.requireMls().archiveOpen(PREFS_ADDRESS, r.version, fromB64(r.blob))));
    return { version: r.version, prefs: PersonalPrefs.parse(json) };
  }
  async loadPrefs(force = false) {
    if (this.prefsCache && !force) return this.prefs;
    this.prefsCache = this.openPrefs(await this.api.getPrefs());
    this.emit("prefs");
    return this.prefs;
  }
  /**
   * Sửa tùy chọn: `mutate` nhận bản sao và sửa tại chỗ (hoặc trả bản mới). Ghi lạc quan theo version; thiết bị khác ghi
   * trước (409) → đọc bản của họ, áp lại `mutate` rồi ghi lại (tối đa 6 lần). Các lần gọi trên cùng client xếp hàng.
   */
  updatePrefs(mutate) {
    const run = async () => {
      for (let attempt = 0; attempt < 6; attempt++) {
        if (!this.prefsCache) this.prefsCache = this.openPrefs(await this.api.getPrefs());
        const base = this.prefsCache;
        const draft = structuredClone(base.prefs);
        const next = PersonalPrefs.parse(mutate(draft) ?? draft);
        const bytes = utf8.encode(JSON.stringify(next));
        if (bytes.length > MAX_PREFS_BYTES) throw new Error("T\xF9y ch\u1ECDn qu\xE1 l\u1EDBn");
        this.prefsPending = next;
        this.emit("prefs");
        try {
          const sealed = this.requireMls().archiveSeal(PREFS_ADDRESS, base.version + 1, bytes);
          const { version } = await this.api.putPrefs(base.version, toB64(sealed));
          this.prefsCache = { version, prefs: next };
          this.prefsPending = void 0;
          this.emit("prefs");
          return next;
        } catch (e) {
          if (e instanceof SwChatApiError && e.status === 409) {
            const current = PrefsResponse2.safeParse(e.body?.current);
            this.prefsCache = current.success ? this.openPrefs(current.data) : void 0;
            continue;
          }
          this.prefsPending = void 0;
          this.emit("prefs");
          throw e;
        }
      }
      this.prefsPending = void 0;
      this.emit("prefs");
      throw new Error("Kh\xF4ng l\u01B0u \u0111\u01B0\u1EE3c t\xF9y ch\u1ECDn (xung \u0111\u1ED9t li\xEAn t\u1EE5c)");
    };
    const result = this.prefsQueue.then(run, run);
    this.prefsQueue = result.catch(() => void 0);
    return result;
  }
  // =====================================================================
  // Archive
  // =====================================================================
  async uploadArchive(conv, items) {
    if (conv.secret || !items.length) return;
    const mls = this.requireMls();
    const entries = items.map((i) => ({
      conversation_id: conv.id,
      seq: i.seq,
      body: toB64(mls.archiveSeal(conv.id, i.seq, i.plaintext))
    }));
    for (let i = 0; i < entries.length; i += 500) {
      await this.api.uploadArchive(entries.slice(i, i + 500)).catch((e) => this.log.warn(`archive upload: ${e}`));
    }
  }
  /** Thiết bị mới: dựng lại lịch sử từ archive (04 §4). */
  async importArchive() {
    await this.serial(async () => {
      const mls = this.requireMls();
      const ids = await this.api.archivedConversations();
      for (const conversationId of ids) {
        const state = await this.opts.store.getConversationState(conversationId);
        let after = state.archive_cursor;
        for (; ; ) {
          const page = await this.api.archive(conversationId, after);
          for (const entry of page.entries) {
            after = entry.seq;
            try {
              const archived = JSON.parse(utf8.decode(mls.archiveOpen(conversationId, entry.seq, fromB64(entry.body))));
              const payload = MessagePayload.safeParse(archived.payload);
              if (!payload.success) continue;
              await this.applyPayload(
                conversationId,
                payload.data,
                archived.sender_user_id,
                archived.sender_device_id,
                entry.seq,
                trustedTs(payload.data.ts, archived.created_at),
                true
              );
            } catch (e) {
              this.log.warn(`archive ${conversationId}#${entry.seq}: ${e instanceof Error ? e.message : e}`);
            }
          }
          if (!page.has_more) break;
        }
        const latest = await this.opts.store.getConversationState(conversationId);
        await this.opts.store.setConversationState(conversationId, { ...latest, archive_cursor: after });
        this.emit("messages", conversationId);
      }
    });
  }
  // =====================================================================
  // Realtime
  // =====================================================================
  connectRealtime() {
    if (this.opts.realtime === false || this.ws || !this.session || this.disposed) return;
    const WS = this.opts.WebSocket ?? globalThis.WebSocket;
    if (!WS) return;
    const url = `${this.opts.baseUrl.replace(/^http/, "ws")}/v1/ws`;
    const ws = new WS(url);
    this.ws = ws;
    ws.onopen = async () => {
      this.wsAttempts = 0;
      const token = await this.accessToken().catch(() => void 0);
      if (token) ws.send(JSON.stringify({ t: "auth", token }));
    };
    ws.onmessage = (ev) => {
      const parsed = ServerEvent.safeParse(JSON.parse(String(ev.data)));
      if (parsed.success) this.onServerEvent(parsed.data);
    };
    ws.onclose = () => {
      if (this.ws === ws) this.ws = void 0;
      if (this.disposed) return;
      if (this.stopped && this.statusValue === "ready") return;
      if (this.statusValue === "revoked" || this.statusValue === "signed_out") return;
      const delay = Math.min(3e4, 1e3 * 2 ** this.wsAttempts++);
      clearTimeout(this.wsRetry);
      this.wsRetry = setTimeout(() => {
        this.wsRetry = void 0;
        if (this.disposed) return;
        this.connectRealtime();
        void this.syncAll().catch((e) => this.log.warn(`sync: ${e}`));
      }, delay);
    };
  }
  onServerEvent(e) {
    const bg = (p) => void p.catch((err) => this.log.warn(`${e.t}: ${err}`));
    switch (e.t) {
      case "msg":
        bg(this.syncConversation(e.msg.conversation_id));
        break;
      case "welcome":
      case "conv":
        bg(
          this.serial(async () => {
            await this.processWelcomes();
            await this.refreshConversations();
            await this.syncConversationInner(e.conversation_id);
          })
        );
        break;
      case "members_changed":
        bg(
          this.serial(async () => {
            await this.refreshConversations();
            await this.syncConversationInner(e.conversation_id);
          }).then(() => {
            this.emit("messages", e.conversation_id);
            this.scheduleCleanup(e.conversation_id);
          })
        );
        break;
      case "conv_deleted":
        this.clearConversationTimers(e.conversation_id);
        bg(
          this.serial(async () => {
            await this.purgeLocalConversation(e.conversation_id);
            await this.refreshConversations();
          })
        );
        break;
      case "deleted":
        bg(this.serial(() => this.markDeletedBySeq(e.conversation_id, e.seqs)).then(() => this.emit("messages", e.conversation_id)));
        break;
      case "typing":
        this.emit("typing", e.conversation_id, e.user_id);
        break;
      case "profile":
        bg(this.loadUsers().then(() => this.emit("conversations"), () => void 0));
        break;
      case "device_list":
        this.scheduleRepair(e.user_id);
        break;
      case "announcement":
        this.emit("announcement", e.id);
        break;
      case "calendar":
        this.emit("calendar", e.id);
        break;
      case "reminder":
        this.emit("reminder", e);
        break;
      case "prefs":
        if (!this.prefsCache || e.version > this.prefsCache.version) bg(this.loadPrefs(true).then(() => void 0, () => void 0));
        break;
      case "link_request":
        if (this.pendingLink?.linkId === e.id) this.pendingLink.resolve();
        else this.emit("link_request", e.id);
        break;
      case "revoked":
        bg(
          (async () => {
            await this.shutdown();
            await this.opts.store.clear();
            this.setStatus("revoked");
          })()
        );
        break;
      default:
        break;
    }
  }
};
var MEMBERSHIP_KINDS = /* @__PURE__ */ new Set(["member_added", "member_removed", "member_left"]);
function threadRootOf(p) {
  const root = p.body.thread_root;
  return root && root !== p.id ? root : void 0;
}
function departedDevices(conv, inGroup) {
  const active = new Set(conv.members.map((m) => m.user_id));
  return inGroup.filter((m) => !active.has(m.user_id)).map((m) => [m.user_id, m.device_id]);
}
function totalUnreadOf(conversations) {
  return conversations.reduce((n, c) => c.left ? n : n + c.unread_count, 0);
}
function readStatusOf(msg, receipts, me) {
  const out = { read: [], delivered: [] };
  if (!msg || msg.seq === null) return out;
  for (const [userId, r] of Object.entries(receipts)) {
    if (userId === msg.sender_user_id || userId === me) continue;
    if (r.read_seq >= msg.seq) out.read.push(userId);
    if (r.delivered_seq >= msg.seq) out.delivered.push(userId);
  }
  return out;
}
var CALL_OUTCOME = {
  completed: "",
  missed: " nh\u1EE1",
  declined: " b\u1ECB t\u1EEB ch\u1ED1i",
  canceled: " \u0111\xE3 h\u1EE7y",
  busy: " \xB7 m\xE1y b\u1EADn",
  failed: " kh\xF4ng k\u1EBFt n\u1ED1i \u0111\u01B0\u1EE3c"
};
function callLabel(c) {
  const d = c.duration ?? 0;
  const time = c.outcome === "completed" ? ` \xB7 ${Math.floor(d / 60)}:${String(d % 60).padStart(2, "0")}` : "";
  return `\u{1F4DE} Cu\u1ED9c g\u1ECDi ${c.video ? "video" : "tho\u1EA1i"}${CALL_OUTCOME[c.outcome]}${time}`;
}
function locationLabel(m) {
  return `\u{1F4CD} ${m.text || (m.location ? `${m.location.lat.toFixed(5)}, ${m.location.lng.toFixed(5)}` : "V\u1ECB tr\xED")}`;
}
function fileLabel(m) {
  return `\u{1F4CE} ${m.file?.name ?? "T\u1EC7p \u0111\xEDnh k\xE8m"}`;
}

// src/storage.ts
var MemoryStore = class {
  session;
  convState = /* @__PURE__ */ new Map();
  msgs = /* @__PURE__ */ new Map();
  pins = /* @__PURE__ */ new Map();
  activity = /* @__PURE__ */ new Map();
  async loadSession() {
    return this.session;
  }
  async saveSession(session) {
    this.session = session;
  }
  async clear() {
    this.session = void 0;
    this.convState.clear();
    this.msgs.clear();
    this.pins.clear();
    this.activity.clear();
  }
  async getConversationState(id) {
    return this.convState.get(id) ?? { cursor: 0, archive_cursor: 0 };
  }
  async setConversationState(id, state) {
    this.convState.set(id, state);
  }
  async putMessages(messages) {
    for (const m of messages) {
      let conv = this.msgs.get(m.conversation_id);
      if (!conv) this.msgs.set(m.conversation_id, conv = /* @__PURE__ */ new Map());
      conv.set(m.id, m);
    }
  }
  async getMessage(conversationId, id) {
    return this.msgs.get(conversationId)?.get(id);
  }
  async listMessages(conversationId, opts) {
    const all = [...this.msgs.get(conversationId)?.values() ?? []].sort(compareMessages);
    return opts?.limit ? all.slice(-opts.limit) : all;
  }
  async getPinnedUsk(userId) {
    return this.pins.get(userId);
  }
  async setPinnedUsk(userId, uskPub) {
    this.pins.set(userId, uskPub);
  }
  async getActivity(conversationId) {
    return this.activity.get(conversationId) ?? [];
  }
  async setActivity(conversationId, items) {
    this.activity.set(conversationId, items);
  }
  async deleteConversationData(conversationId) {
    this.msgs.delete(conversationId);
    this.activity.delete(conversationId);
  }
};
function compareMessages(a, b) {
  const sa = a.seq ?? Number.MAX_SAFE_INTEGER;
  const sb = b.seq ?? Number.MAX_SAFE_INTEGER;
  return sa - sb || a.ts - b.ts;
}

// src/richtext.ts
var MARKERS = [
  { m: "**", t: "bold" },
  { m: "__", t: "underline" },
  { m: "~~", t: "strike" },
  { m: "||", t: "spoiler" },
  { m: "*", t: "italic" },
  { m: "_", t: "italic" }
];
var ESCAPABLE = "\\*_~|`[>";
var LINK_RE = /^\[([^\]\n]+)\]\((https?:\/\/[^\s)]+|mailto:[^\s)]+)\)/i;
var ALNUM = /[\p{L}\p{N}]/u;
var SPACE = /\s/;
function tokenize(input) {
  const tokens = [];
  let buf = "";
  const flush = () => {
    if (buf) tokens.push({ k: "text", s: buf });
    buf = "";
  };
  let lineStart = true;
  let i = 0;
  while (i < input.length) {
    const c = input[i];
    const startOfLine = lineStart;
    lineStart = false;
    if (c === "\n") {
      flush();
      tokens.push({ k: "nl" });
      lineStart = true;
      i++;
      continue;
    }
    if (c === "\\" && i + 1 < input.length && ESCAPABLE.includes(input[i + 1])) {
      buf += input[i + 1];
      i += 2;
      continue;
    }
    if (input.startsWith("```", i)) {
      const close = input.indexOf("```", i + 3);
      if (close > i + 3) {
        let s = input.slice(i + 3, close);
        if (s.startsWith("\n")) s = s.slice(1);
        if (s.endsWith("\n")) s = s.slice(0, -1);
        if (s) {
          flush();
          tokens.push({ k: "span", t: "pre", s });
          i = close + 3;
          continue;
        }
      }
    }
    if (c === "`") {
      const close = input.indexOf("`", i + 1);
      if (close > i + 1 && !input.slice(i + 1, close).includes("\n")) {
        flush();
        tokens.push({ k: "span", t: "code", s: input.slice(i + 1, close) });
        i = close + 1;
        continue;
      }
    }
    if (c === "[") {
      const m = LINK_RE.exec(input.slice(i, i + 2200));
      if (m) {
        flush();
        tokens.push({ k: "span", t: "link", s: m[1], url: m[2] });
        i += m[0].length;
        continue;
      }
    }
    if (startOfLine && c === ">" && input[i + 1] === " ") {
      flush();
      tokens.push({ k: "quote" });
      i += 2;
      continue;
    }
    const mk = MARKERS.find(({ m }) => input.startsWith(m, i));
    if (mk) {
      const prev = i > 0 ? input[i - 1] : void 0;
      const next = input[i + mk.m.length];
      const word = mk.m.startsWith("_");
      const lone = mk.m === "*";
      const canOpen = next !== void 0 && !SPACE.test(next) && !(word && prev !== void 0 && ALNUM.test(prev)) && !(lone && (next === "*" || prev === "*"));
      const canClose = prev !== void 0 && !SPACE.test(prev) && !(word && next !== void 0 && ALNUM.test(next)) && !(lone && (next === "*" || prev === "*"));
      if (canOpen || canClose) {
        flush();
        tokens.push({ k: "mark", m: mk.m, t: mk.t, canOpen, canClose });
        i += mk.m.length;
        continue;
      }
      buf += input.slice(i, i + mk.m.length);
      i += mk.m.length;
      continue;
    }
    buf += c;
    i++;
  }
  flush();
  return tokens;
}
function matchPairs(tokens) {
  const stack = [];
  const hasContent = (from, to) => tokens.slice(from + 1, to).some((t) => t.k !== "nl" && !(t.k === "text" && !t.s));
  const closeQuote = (at) => {
    let q = stack.length - 1;
    while (q >= 0 && tokens[stack[q]].k !== "quote") q--;
    if (q < 0) return;
    const qi = stack[q];
    stack.length = q;
    if (hasContent(qi, at)) {
      const open = tokens[qi];
      open.pair = at;
      open.role = "open";
    }
  };
  tokens.forEach((t, i) => {
    if (t.k === "quote") stack.push(i);
    else if (t.k === "nl") closeQuote(i);
    else if (t.k === "mark") {
      if (t.canClose) {
        let at = -1;
        for (let s = stack.length - 1; s >= 0; s--) {
          const o = tokens[stack[s]];
          if (o.k === "quote") break;
          if (o.k === "mark" && o.m === t.m) {
            at = s;
            break;
          }
        }
        if (at >= 0 && hasContent(stack[at], i)) {
          const open = tokens[stack[at]];
          open.pair = i;
          open.role = "open";
          t.pair = stack[at];
          t.role = "close";
          stack.length = at;
          return;
        }
      }
      if (t.canOpen) stack.push(i);
    }
  });
  closeQuote(tokens.length);
}
function parseMarkup(input) {
  const tokens = tokenize(input);
  matchPairs(tokens);
  let out = "";
  const entities = [];
  const starts = /* @__PURE__ */ new Map();
  const quoteEnds = /* @__PURE__ */ new Map();
  const endQuote = (at) => {
    const o = quoteEnds.get(at);
    if (o !== void 0 && out.length > o) entities.push({ t: "quote", o, l: out.length - o });
  };
  tokens.forEach((t, i) => {
    switch (t.k) {
      case "text":
        out += t.s;
        break;
      case "nl":
        endQuote(i);
        out += "\n";
        break;
      case "span": {
        const o = out.length;
        out += t.s;
        entities.push(t.t === "link" ? { t: "link", o, l: t.s.length, url: t.url } : { t: t.t, o, l: t.s.length });
        break;
      }
      case "quote":
        if (t.role === "open") quoteEnds.set(t.pair, out.length);
        else out += "> ";
        break;
      case "mark":
        if (t.pair === void 0) out += t.m;
        else if (t.role === "open") starts.set(i, out.length);
        else {
          const o = starts.get(t.pair);
          if (out.length > o) entities.push({ t: t.t, o, l: out.length - o });
        }
        break;
    }
  });
  endQuote(tokens.length);
  entities.sort((a, b) => a.o - b.o || b.l - a.l);
  return { text: out, entities: sanitizeEntities(out, entities) ?? [] };
}
var OPEN = { bold: "**", italic: "_", underline: "__", strike: "~~", spoiler: "||", quote: "> " };
function escapePlain(s, prevChar, nextChar) {
  let out = "";
  for (let i = 0; i < s.length; i++) {
    const c = s[i];
    const prev = i > 0 ? s[i - 1] : prevChar;
    const next = i + 1 < s.length ? s[i + 1] : nextChar;
    const special = c === "\\" || c === "*" || c === "`" || c === "[" || c === "~" || c === "|" && (prev === "|" || next === "|") || c === "_" && (!prev || !ALNUM.test(prev) || !next || !ALNUM.test(next)) || c === ">" && (i === 0 ? prevChar === void 0 || prevChar === "\n" : prev === "\n") && next === " ";
    out += special ? `\\${c}` : c;
  }
  return out;
}
function toMarkup(text, entities) {
  const ents = [...sanitizeEntities(text, entities) ?? []].sort((a, b) => a.o - b.o || b.l - a.l);
  const render = (from, to, list) => {
    let out = "";
    let pos = from;
    let i = 0;
    while (i < list.length) {
      const e = list[i];
      if (e.o < pos) {
        i++;
        continue;
      }
      out += escapePlain(text.slice(pos, e.o), text[pos - 1], text[e.o]);
      const end = Math.min(e.o + e.l, to);
      const raw = text.slice(e.o, end);
      let j = i + 1;
      while (j < list.length && list[j].o < end) j++;
      const inner = list.slice(i + 1, j);
      if (e.t === "code") out += `\`${raw}\``;
      else if (e.t === "pre") out += `\`\`\`
${raw}
\`\`\``;
      else if (e.t === "link") out += `[${raw.replace(/[\]\\]/g, "\\$&")}](${e.url})`;
      else if (e.t === "quote") out += `${OPEN.quote}${render(e.o, end, inner)}`;
      else out += `${OPEN[e.t]}${render(e.o, end, inner)}${OPEN[e.t]}`;
      pos = end;
      i = j;
    }
    return out + escapePlain(text.slice(pos, to), text[pos - 1], text[to]);
  };
  return render(0, text.length, ents);
}
function segmentEntities(text, entities) {
  const list = [...sanitizeEntities(text, entities) ?? []].sort((a, b) => a.o - b.o || b.l - a.l);
  const build = (ents, from, to) => {
    const nodes = [];
    let pos = from;
    let i = 0;
    while (i < ents.length) {
      const e = ents[i];
      if (e.o < pos) {
        i++;
        continue;
      }
      if (e.o > pos) nodes.push({ t: "text", text: text.slice(pos, e.o) });
      const end = Math.min(e.o + e.l, to);
      let j = i + 1;
      while (j < ents.length && ents[j].o < end) j++;
      const opaque = e.t === "code" || e.t === "pre" || e.t === "link";
      nodes.push({
        t: e.t,
        url: e.url,
        children: opaque ? [{ t: "text", text: text.slice(e.o, end) }] : build(ents.slice(i + 1, j), e.o, end)
      });
      pos = end;
      i = j;
    }
    if (pos < to) nodes.push({ t: "text", text: text.slice(pos, to) });
    return nodes;
  };
  return build(list, 0, text.length);
}
export {
  ApiClient,
  FILE_CHUNK_SIZE,
  FILE_TAG_SIZE,
  MAX_FILE_SIZE,
  MemoryStore,
  PersonalLibrary,
  SwChatApiError,
  SwChatClient,
  THREAD_PREVIEW_PREFIX,
  bytesEqual,
  callLabel,
  compareMessages,
  detectMentions,
  fileLabel,
  fromB64,
  hasAllMention,
  isRateLimited,
  locationLabel,
  normalizeSearch,
  parseMarkup,
  readStatusOf,
  sanitizeEntities,
  segmentEntities,
  toB64,
  toMarkup,
  totalUnreadOf,
  trustedTs,
  utf8,
  uuidv4,
  uuidv7
};
